import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { Breadcrumb, Button, Form, Modal, Progress, Slider, Switch, message, Tooltip } from "antd";
import { useCookies } from "react-cookie";
import dayjsInstance from "../utils/dayjs";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import axios from "../utils/axios";
import { textToIPAPure } from "../common/ipaPure";
import {
    AudioFilled, SoundOutlined,
    StepBackwardOutlined, StepForwardOutlined, PlayCircleFilled, PauseCircleFilled,
    SettingOutlined, HomeOutlined, CheckOutlined, EyeOutlined, EyeInvisibleOutlined,
    RetweetOutlined, FontSizeOutlined, VideoCameraOutlined, CheckCircleOutlined
} from "@ant-design/icons";
import repeatTrack from "../component/icon/repeat-track.png";
import convertTimeToMilliseconds from "../utils/convertTimeToMilliseconds";
import toLowerCamelCase from "../utils/toLowerCamelCase";
import { createUserListening, getUserListening } from "../apis/userListening";
import { alignTranscript } from "../utils/alignTranscript";

export default function Listening() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const index = location?.state?.index;
    const [form] = Form.useForm();
    const audioElem = useRef();
    const videoElem = useRef();
    const lyricsContainerRef = useRef();

    const [dataLesson, setdataLesson] = useState({});
    const [options, setOptions] = useState([]);
    const [story, setStory] = useState("Main Story");
    const [dataUserListening, setDataUserListening] = useState([]);
    // removed modal state for transcript checking
    const [isPlaying, setisPlaying] = useState(false);
    const [currentTime, setcurrentTime] = useState(0);
    const [speedMode, setSpeedMode] = useState(1);
    const [active, setActive] = useState(0);
    const [playAll, setPlayAll] = useState(false);
    const [timestamps, setTimestamps] = useState({});
    const [duration, setDuration] = useState(0);
    const [count, setCount] = useState(1);
    const [replay, setReplay] = useState(1);
    const [timeReplay, setTimeReplay] = useState(0.5);
    const [timeoutPlay, setTimeoutPlay] = useState(null);
    const [lyric, setLyric] = useState([]);
    const [showSettings, setShowSettings] = useState(true);
    const [totalDuration, setTotalDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [subtitleFontSize, setSubtitleFontSize] = useState(16);
    const [translationFontSize, setTranslationFontSize] = useState(14);
    const [showTranslation, setShowTranslation] = useState(true);
    const [autoScroll, setAutoScroll] = useState(true);
    const isSeekingRef = useRef(false);
    const wholeReplayCountRef = useRef(0);
    const shadowReadingRef = useRef(false);
    const fullTranscriptRef = useRef(''); // accumulates all speech during shadow-reading
    const lastTranscriptRef = useRef('');
    const autoAdvanceLSTimerRef = useRef(null); // timer for lyric auto-advance (no-audio fallback)
    const playSentenceIntervalRef = useRef(null); // interval for per-sentence playback stop
    const sentenceRepeatCountRef = useRef({}); // tracks how many times current sentence has been repeated during current play
    const sentenceRepeatTSRef = useRef({}); // timestamp (ms) when last repeat was triggered per sentence
    const reloadOnEndRef = useRef(false);
    const lastSeekValueRef = useRef(0);
    const lyricRef = useRef(lyric);
    const activeRef = useRef(active);
    const speedModeRef = useRef(speedMode);
    const storyRef = useRef(story);

    useEffect(() => {
        const handleGlobalMouseUp = () => { isSeekingRef.current = false; };
        window.addEventListener('mouseup', handleGlobalMouseUp);
        window.addEventListener('touchend', handleGlobalMouseUp);
        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            window.removeEventListener('touchend', handleGlobalMouseUp);
        };
    }, []);

    useEffect(() => { lyricRef.current = lyric; }, [lyric]);
    useEffect(() => { activeRef.current = active; }, [active]);
    useEffect(() => { speedModeRef.current = speedMode; }, [speedMode]);
    useEffect(() => { storyRef.current = story; }, [story]);

    const triggerReloadOnEnd = (immediate = false) => {
        if (reloadOnEndRef.current) return;
        reloadOnEndRef.current = true;

        // Save settings immediately before reload to ensure nothing is lost
        try {
            const cfg = {
                speedMode: speedModeRef.current,
                subtitleFontSize,
                translationFontSize,
                showTranslation,
                showIPA,
                showTranscript,
                autoScroll,
                volume,
                showSettings,
                story: storyRef.current,
                loopMode,
                repeatWholeTrack,
                replay,
                sentenceRepeatLimit
            };
            localStorage.setItem(SETTINGS_KEY, JSON.stringify(cfg));
        } catch (e) { }

        // Set a flag to auto-start playing after reload if we were in a loop mode
        if (repeatWholeTrack || (replay && replay > 1)) {
            localStorage.setItem(SETTINGS_KEY + '_autoplay', 'true');
        }

        setTimeout(() => {
            try {
                window.location.reload();
                return;
            } catch (e) {
                try { window.location.replace(window.location.href); } catch (err) { }
            }
            reloadOnEndRef.current = false;
        }, immediate ? 0 : 50);
    };

    const [translations, setTranslations] = useState([]);
    const [showIPA, setShowIPA] = useState(false);
    const [translationLoading, setTranslationLoading] = useState(false);
    // IPA cache: compute lazily for visible lines, cache results to avoid recompute
    const ipaCacheRef = useRef(new Map());
    const getIpaForIndex = (i) => {
        const line = (lyric && lyric[i]) || '';
        return ipaCacheRef.current.has(line) ? ipaCacheRef.current.get(line) : null;
    };

    const scheduleComputeIpa = (indices) => {
        if (!Array.isArray(lyric)) return;
        // compute in small async batches to avoid blocking
        const toCompute = [];
        for (const i of indices) {
            const line = lyric[i] || '';
            if (!ipaCacheRef.current.has(line)) toCompute.push({ i, line });
        }
        if (!toCompute.length) return;
        // use setTimeout to yield to UI thread and request IPA from backend API
        setTimeout(async () => {
            try {
                // Compute IPA locally in the frontend using pure-JS converter
                for (const { line } of toCompute) {
                    try { ipaCacheRef.current.set(line, textToIPAPure(line || '')); } catch (err) { ipaCacheRef.current.set(line, ''); }
                }
            } catch (e) {
                for (const { line } of toCompute) {
                    ipaCacheRef.current.set(line, '');
                }
            }
            // trigger render
            try { setTranslations(t => [...t]); } catch (e) { }
        }, 20);
    };
    const [ipaReady, setIpaReady] = useState(false);
    const [goalDays, setGoalDays] = useState(30);
    const [goalHours, setGoalHours] = useState(30);
    const [editingGoals, setEditingGoals] = useState(false);
    // Shadow-reading mode: play audio + listen simultaneously, lyric follows audio
    const [isBatchScoring, setIsBatchScoring] = useState(false);
    const [showScoreModal, setShowScoreModal] = useState(false);
    const [scoreResult, setScoreResult] = useState(null); // { avgPoint, lines: [{lyric, text, point}] }
    const [playingSentence, setPlayingSentence] = useState(null); // index of sentence being played solo
    // Control visibility of transcript globally and per-line
    const [showTranscript, setShowTranscript] = useState(true); // global toggle to show/hide transcript
    const [hiddenLines, setHiddenLines] = useState({}); // map of lineIndex -> true if hidden
    const [loopMode, setLoopMode] = useState(0); // 0: no loop, 1: loop single
    const [repeatWholeTrack, setRepeatWholeTrack] = useState(false); // separate state for whole lesson repeat
    const [sentenceRepeatLimit, setSentenceRepeatLimit] = useState(1); // 1, 2, 3, or 0 (Infinite)

    // Persist listening settings so they survive reloads
    const SETTINGS_KEY = 'listening_settings_v1';
    useEffect(() => {
        try {
            const raw = localStorage.getItem(SETTINGS_KEY);
            if (!raw) return;
            const cfg = JSON.parse(raw) || {};
            if (typeof cfg.speedMode === 'number') setSpeedMode(cfg.speedMode);
            if (typeof cfg.subtitleFontSize === 'number') setSubtitleFontSize(cfg.subtitleFontSize);
            if (typeof cfg.translationFontSize === 'number') setTranslationFontSize(cfg.translationFontSize);
            if (typeof cfg.showTranslation === 'boolean') setShowTranslation(cfg.showTranslation);
            if (typeof cfg.showIPA === 'boolean') setShowIPA(cfg.showIPA);
            if (typeof cfg.showTranscript === 'boolean') setShowTranscript(cfg.showTranscript);
            if (typeof cfg.autoScroll === 'boolean') setAutoScroll(cfg.autoScroll);
            if (typeof cfg.volume === 'number') setVolume(cfg.volume);
            if (typeof cfg.showSettings === 'boolean') setShowSettings(cfg.showSettings);
            if (typeof cfg.story === 'string' && cfg.story) setStory(cfg.story);
            if (typeof cfg.loopMode === 'number') setLoopMode(cfg.loopMode);
            if (typeof cfg.repeatWholeTrack === 'boolean') setRepeatWholeTrack(cfg.repeatWholeTrack);
            if (typeof cfg.replay === 'number') setReplay(cfg.replay);
            if (typeof cfg.sentenceRepeatLimit === 'number') setSentenceRepeatLimit(cfg.sentenceRepeatLimit);
        } catch (e) {
            // ignore malformed localStorage
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const cfg = {
            speedMode,
            subtitleFontSize,
            translationFontSize,
            showTranslation,
            showIPA,
            showTranscript,
            autoScroll,
            volume,
            showSettings,
            story,
            loopMode,
            repeatWholeTrack,
            replay,
            sentenceRepeatLimit
        };
        try {
            localStorage.setItem(SETTINGS_KEY, JSON.stringify(cfg));
        } catch (e) {
            // ignore storage errors
        }
    }, [speedMode, subtitleFontSize, translationFontSize, showTranslation, showIPA, showTranscript, autoScroll, volume, showSettings, story, loopMode, repeatWholeTrack, replay, sentenceRepeatLimit]);
    // When user enables "lặp 1 câu", ensure current active sentence is allowed to repeat
    useEffect(() => {
        if (loopMode === 1) {
            try { sentenceRepeatCountRef.current[active] = 0; } catch (e) { }
            try { sentenceRepeatTSRef.current[active] = 0; } catch (e) { }
        }
    }, [loopMode, active]);
    // Handles automatic playback after a reload caused by triggerReloadOnEnd
    useEffect(() => {
        const handleAutoPlayAfterReload = async () => {
            const shouldAuto = localStorage.getItem(SETTINGS_KEY + '_autoplay') === 'true';
            if (shouldAuto) {
                localStorage.removeItem(SETTINGS_KEY + '_autoplay');
                // Wait for media and transcript to be ready
                if (dataLesson?.id && lyric?.length > 0) {
                    const media = getMediaElem();
                    if (media) {
                        try {
                            media.currentTime = 0;
                            media.playbackRate = speedModeRef.current;
                            await media.play();
                            setisPlaying(true);
                            setPlayAll(true);
                        } catch (e) {
                            console.warn('Autoplay after reload blocked or failed:', e);
                        }
                    }
                }
            }
        };
        const tid = setTimeout(handleAutoPlayAfterReload, 1200);
        return () => clearTimeout(tid);
    }, [dataLesson, lyric]); // eslint-disable-line react-hooks/exhaustive-deps

    const [showDrillMode, setShowDrillMode] = useState(false); // "Luyện nghe" drill mode
    const [drillIndex, setDrillIndex] = useState(0); // current sentence in drill mode
    const [userInfo, setUserInfo] = useState(null); // fresh user data from API (vip_expire_at)
    const [drillIsPlaying, setDrillIsPlaying] = useState(false); // audio playing in drill mode
    const [drillAnswer, setDrillAnswer] = useState(''); // user typed answer in drill mode
    const [drillAnswerResult, setDrillAnswerResult] = useState(null); // { point, words } after check
    const [drillListening, setDrillListening] = useState(false); // voice input active for drill mode
    const [drillRecording, setDrillRecording] = useState(false); // audio recording in drill mode
    const [drillVideoRecording, setDrillVideoRecording] = useState(false); // video recording in drill mode
    const [drillMediaRecorder, setDrillMediaRecorder] = useState(null); // MediaRecorder for drill mode
    const [drillRecordingTranscript, setDrillRecordingTranscript] = useState(''); // accumulated transcript during recording
    const [showDrillRecordingModal, setShowDrillRecordingModal] = useState(false); // show result after recording
    const drillCameraPreviewRef = useRef(null); // camera preview for drill mode
    const drillCameraStreamRef = useRef(null); // camera stream for drill mode
    const perSentenceVideoRecorderRef = useRef(null);
    const perSentenceCameraStreamRef = useRef(null);
    const [perSentenceVideoMode, setPerSentenceVideoMode] = useState(false);

    const { transcript: drillTranscript, listening: isDrillMicActive, resetTranscript: resetDrillTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();

    // Ensure drill camera stream is assigned to video element after render
    useEffect(() => {
        if (drillVideoRecording && drillCameraStreamRef.current && drillCameraPreviewRef.current) {
            if (drillCameraPreviewRef.current.srcObject !== drillCameraStreamRef.current) {
                drillCameraPreviewRef.current.srcObject = drillCameraStreamRef.current;
                drillCameraPreviewRef.current.play().catch(err => console.log('Video play handled:', err));
            }
        }
    }, [drillVideoRecording]);

    const [showDrillSettings, setShowDrillSettings] = useState(true); // settings panel in drill mode
    const [drillAutoNext, setDrillAutoNext] = useState(false); // auto-advance to next sentence after playback
    const [drillRepeat, setDrillRepeat] = useState(1); // number of times to repeat each sentence (1 = no repeat)
    const drillRepeatCountRef = useRef(0); // tracks how many times current sentence has played
    const drillIntervalRef = useRef(null); // interval for stopping drill playback at sentence end
    const drillEndRef = useRef(null); // end time (seconds) of current drill sentence
    const drillAutoNextRef = useRef(drillAutoNext);
    const drillRepeatRef = useRef(drillRepeat);
    const drillIndexRef = useRef(0); // mirror of drillIndex for use inside intervals
    useEffect(() => { drillAutoNextRef.current = drillAutoNext; }, [drillAutoNext]);
    useEffect(() => { drillRepeatRef.current = drillRepeat; }, [drillRepeat]);

    // Sync voice transcript to drill answer input
    useEffect(() => {
        if (drillListening && drillTranscript) {
            setDrillAnswer(drillTranscript);
            // Accumulate transcript during video recording
            if (drillVideoRecording || drillRecording) {
                setDrillRecordingTranscript(prev => {
                    const newText = drillTranscript.trim();
                    if (!prev || !newText) return newText;
                    // Append new words that aren't already at the end
                    const prevWords = prev.toLowerCase().split(/\s+/);
                    const newWords = newText.toLowerCase().split(/\s+/);
                    const lastPrevWord = prevWords[prevWords.length - 1];
                    const firstNewWord = newWords[0];
                    if (firstNewWord === lastPrevWord) {
                        // Likely continuation, merge
                        return prev + ' ' + newText.split(/\s+/).slice(1).join(' ');
                    }
                    return prev + ' ' + newText;
                });
            }
        }
    }, [drillTranscript, drillListening, drillVideoRecording, drillRecording]);

    // Stop listening when isDrillMicActive becomes false
    useEffect(() => {
        if (!isDrillMicActive && drillListening) {
            setDrillListening(false);
        }
    }, [isDrillMicActive, drillListening]);

    const speeds = [0.5, 0.75, 0.8, 0.9, 1, 1.25, 1.5, 2];

    const isAudioUrl = (url) => {
        if (!url) return false;
        if (typeof url !== 'string') return false;
        return /\.(mp3|m4a|wav|ogg|webm|aac)(\?.*)?$/i.test(url) || /^data:audio\//.test(url);
    };

    const isVideoUrl = (url) => {
        if (!url) return false;
        if (typeof url !== 'string') return false;
        return /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(url) || /^data:video\//.test(url);
    };

    const resolveVideo = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return isVideoUrl(val) ? val : null;
        if (typeof val === 'object') {
            if (Array.isArray(val) && val.length) {
                const first = val[0];
                if (typeof first === 'string') return isVideoUrl(first) ? first : null;
                if (first && typeof first.url === 'string') return isVideoUrl(first.url) ? first.url : null;
            }
            if (typeof val.url === 'string') return isVideoUrl(val.url) ? val.url : null;
        }
        return null;
    };

    const proxyIfR2 = (u) => {
        if (!u || typeof u !== 'string') return u;
        if (u.includes('.r2.cloudflarestorage.com')) {
            const proxyBase = (process.env.REACT_APP_API_URL || '');
            return `${proxyBase.replace(/\/$/, '')}/media/proxy?url=${encodeURIComponent(u)}`;
        }
        return u;
    };

    const resolveAudio = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return isAudioUrl(val) ? val : null;
        if (typeof val === 'object') {
            if (Array.isArray(val) && val.length) {
                const first = val[0];
                if (typeof first === 'string') return isAudioUrl(first) ? first : null;
                if (first && typeof first.url === 'string') return isAudioUrl(first.url) ? first.url : null;
            }
            if (typeof val.url === 'string') return isAudioUrl(val.url) ? val.url : null;
        }
        return null;
    };

    const getAudioSrc = () => {
        const src = story === "Main Story" ? resolveAudio(dataLesson?.mainStoryAudio)
            : story === "Vocabulary" ? resolveAudio(dataLesson?.vocabularyAudio)
                : story === "Mini Story" ? resolveAudio(dataLesson?.miniStoryAudio)
                    : story === "POV" ? resolveAudio(dataLesson?.POVAudio)
                        : story === "Comment" ? resolveAudio(dataLesson?.commentAudio)
                            : null;
        if (src) return proxyIfR2(src);
        // Fallback: scan raw payload for any audio-like field
        try {
            const raw = dataLesson?._raw || {};
            for (const v of Object.values(raw)) {
                if (typeof v === 'string' && isAudioUrl(v)) return proxyIfR2(v);
                if (Array.isArray(v) && v.length && typeof v[0] === 'string' && isAudioUrl(v[0])) return proxyIfR2(v[0]);
                if (v && typeof v === 'object' && typeof v.url === 'string' && isAudioUrl(v.url)) return proxyIfR2(v.url);
            }
        } catch (e) { }
        return null;
    };

    const getVideoSrc = () => {
        const src = story === "Main Story" ? resolveVideo(dataLesson?.mainStoryVideo)
            : story === "Vocabulary" ? resolveVideo(dataLesson?.vocabularyVideo)
                : story === "Mini Story" ? resolveVideo(dataLesson?.miniStoryVideo)
                    : story === "POV" ? resolveVideo(dataLesson?.POVVideo)
                        : story === "Comment" ? resolveVideo(dataLesson?.commentVideo)
                            : null;
        if (src) return proxyIfR2(src);
        try {
            const raw = dataLesson?._raw || {};
            for (const v of Object.values(raw)) {
                if (typeof v === 'string' && isVideoUrl(v)) return proxyIfR2(v);
                if (Array.isArray(v) && v.length && typeof v[0] === 'string' && isVideoUrl(v[0])) return proxyIfR2(v[0]);
                if (v && typeof v === 'object' && typeof v.url === 'string' && isVideoUrl(v.url)) return proxyIfR2(v.url);
            }
        } catch (e) { }
        return null;
    };

    const getMediaElem = () => {
        const v = getVideoSrc();
        if (v && videoElem.current) return videoElem.current;
        return audioElem.current;
    };

    // Keep media element volume in sync with UI
    useEffect(() => {
        try {
            const m = getMediaElem();
            if (m) m.volume = Number.isFinite(volume) ? volume : 1;
        } catch (e) { }
    }, [volume]);

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds) || !isFinite(seconds) || seconds <= 0) return '0:00';
        const s = Math.floor(seconds);
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
        return `${m}:${String(sec).padStart(2, '0')}`;
    };

    const getFirstAudioUrl = (normalized) => {
        if (!normalized) return null;
        const fields = ['mainStoryAudio', 'vocabularyAudio', 'miniStoryAudio', 'POVAudio', 'commentAudio'];
        for (const f of fields) {
            const v = normalized[f];
            const a = resolveAudio(v);
            if (a) return a;
        }
        // fallback: scan raw payload for any audio-like value
        try {
            const raw = normalized._raw || {};
            for (const val of Object.values(raw)) {
                if (typeof val === 'string' && isAudioUrl(val)) return val;
                if (Array.isArray(val) && val.length && typeof val[0] === 'string' && isAudioUrl(val[0])) return val[0];
                if (val && typeof val === 'object' && typeof val.url === 'string' && isAudioUrl(val.url)) return val.url;
            }
        } catch (e) { }
        return null;
    };

    const cleanTranslation = (t) => {
        if (!t && t !== '') return t;
        if (typeof t !== 'string') return t;
        return String(t).replace(/^['"“”‘’\s]+|['"“”‘’\s]+$/g, '');
    };

    const fetchFirstAudioDuration = (url) => {
        try {
            if (!url) return;
            const a = new Audio();
            a.preload = 'metadata';
            a.crossOrigin = 'anonymous';
            a.src = url;
            const onLoaded = () => {
                try { const d = a.duration; if (d && isFinite(d) && !isNaN(d) && d > 0) setTotalDuration(prev => (prev !== d ? d : prev)); } catch (e) { }
                try { a.removeEventListener('loadedmetadata', onLoaded); a.src = ''; } catch (e) { }
            };
            const onError = () => { try { a.removeEventListener('error', onError); a.src = ''; } catch (e) { } };
            a.addEventListener('loadedmetadata', onLoaded);
            a.addEventListener('error', onError);
        } catch (e) { /* ignore */ }
    };

    const [cookies] = useCookies(["user"]);
    const { transcript, listening } = useSpeechRecognition();

    // Always skip calling server-side translation ensure endpoint from Listening page
    // (we don't want Listening view to trigger background translation jobs)
    const skipEnsureOnThisPage = true;

    const fetchUserInfo = async () => {
        if (!cookies?.user?.token) return;
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/me`, {
                headers: {
                    'Authorization': `Bearer ${cookies?.user?.token}`
                }
            });
            setUserInfo(response.data);
        } catch (error) {
            console.error('Error fetching user info:', error);
        }
    };

    const isAdmin = () => {
        try {
            if (userInfo && userInfo.role === 1) return true;
            if (cookies && cookies.user && cookies.user.role === 1) return true;
        } catch (e) {
            /* ignore */
        }
        return false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { lesson(); userListening(); fetchUserInfo(); }, []);

    // Load per-lesson goals from localStorage when lesson loads
    useEffect(() => {
        if (!dataLesson?.id) return;
        try {
            const key = `lesson_goal_${dataLesson.id}`;
            const raw = localStorage.getItem(key);
            if (raw) {
                const obj = JSON.parse(raw);
                if (obj?.days) setGoalDays(Number(obj.days));
                if (obj?.hours) setGoalHours(Number(obj.hours));
            } else {
                // defaults
                setGoalDays(30); setGoalHours(30);
            }
        } catch (e) { console.error('Load goals error', e); }
    }, [dataLesson?.id]);

    const saveGoalsForLesson = (days, hours) => {
        if (!dataLesson?.id) return;
        const key = `lesson_goal_${dataLesson.id}`;
        const obj = { days: Number(days || 0), hours: Number(hours || 0) };
        try { localStorage.setItem(key, JSON.stringify(obj)); setGoalDays(obj.days); setGoalHours(obj.hours); message.success('Đã lưu mục tiêu'); }
        catch (e) { console.error('Save goal error', e); message.error('Không thể lưu mục tiêu'); }
    };

    // Auto-open drill mode if navigated from Speaking page
    useEffect(() => {
        if (location?.state?.openDrillMode && dataLesson?.id && lyric?.length > 0 && !showDrillMode) {
            // Check VIP access before opening drill mode
            const vipExpireAt = userInfo?.vip_expire_at || cookies?.user?.vip_expire_at;
            const hasVipAccess = (dayjsInstance(vipExpireAt)?.format("YYYY-MM-DD") > dayjsInstance(Date())?.format("YYYY-MM-DD") && vipExpireAt !== null);
            if (hasVipAccess) {
                // Small delay to ensure audio and data are ready
                setTimeout(() => openDrillMode(), 500);
            } else {
                message.warning('Chức năng Luyện nghe dành cho người dùng VIP. Vui lòng nâng cấp tài khoản!');
            }
        }
    }, [dataLesson, lyric, location?.state?.openDrillMode, showDrillMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cleanup auto-advance timer on unmount
    useEffect(() => {
        return () => {
            clearAutoAdvanceLS();
            if (playSentenceIntervalRef.current) clearInterval(playSentenceIntervalRef.current);
            if (drillIntervalRef.current) clearInterval(drillIntervalRef.current);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // Allow unauthenticated users to view listening page.
        // Only apply lesson access checks when user is authenticated.
        if (!cookies?.user) {
            if (index === undefined) navigate('/');
            return;
        }

        if (dataUserListening?.lesson_id) {
            const vipExpireAt = userInfo?.vip_expire_at || cookies?.user?.vip_expire_at;
            if ((dayjsInstance(vipExpireAt)?.format("YYYY-MM-DD") > dayjsInstance(Date())?.format("YYYY-MM-DD")
                && vipExpireAt !== null)
                || +dataUserListening?.lesson_id >= +dataLesson.id || +index === 0) {
                // allowed
            } else {
                navigate('/');
            }
        }
        if (index === undefined) navigate('/');
    }, [cookies, userInfo]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        addTimestamps();
        const opts = [];
        if (dataLesson?.mainStory?.trim()) opts.push('Main Story');
        if (dataLesson?.vocabulary?.trim()) opts.push('Vocabulary');
        if (dataLesson?.miniStory?.trim()) opts.push('Mini Story');
        if (dataLesson?.POV?.trim()) opts.push('POV');
        if (dataLesson?.comment?.trim()) opts.push('Comment');
        setOptions(opts);

        // Ensure selected story is valid for this lesson's available tracks
        if (opts.length > 0 && story && !opts.includes(story)) {
            setStory(opts[0]);
        }
    }, [dataLesson]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        try {
            if (duration > 0 && count <= replay && !playAll) {
                const timeoutPlayId = setTimeout(() => {
                    safePlay().then(() => {
                        try { audioElem.current.pause(); } catch (e) { }
                    });
                    if (count === replay) {
                        setisPlaying(false); setDuration(0); setCount(1);
                    } else {
                        const timeout = setTimeout(async () => {
                            await play(active); setCount(count + 1); clearTimeout(timeout);
                        }, timeReplay * 1000);
                    }
                    clearTimeout(timeoutPlayId);
                }, duration * 1000 / speedMode);
                setTimeoutPlay(timeoutPlayId);
            }
        } catch (e) { console.log(e); }
    }, [duration, count]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        form.resetFields();
        // Auto-scroll to active lyric line
        if (autoScroll && lyricsContainerRef.current) {
            const activeEl = lyricsContainerRef.current.querySelector(`[data-lyric-index="${active}"]`);
            if (activeEl) {
                activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [active]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { handleLyric(toLowerCamelCase(story)); }, [story, dataLesson]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (listening) {
            // Live: show transcript in textarea (non-shadow only)
            if (!shadowReadingRef.current) {
                form.setFieldsValue({ text: transcript });
            } else {
                // accumulate incremental deltas into fullTranscriptRef to preserve all spoken words
                try {
                    const current = transcript || '';
                    const last = lastTranscriptRef.current || '';
                    if (current.length > last.length) {
                        const delta = current.slice(last.length).trim();
                        if (delta) fullTranscriptRef.current = (fullTranscriptRef.current ? fullTranscriptRef.current + ' ' : '') + delta;
                    }
                    lastTranscriptRef.current = current;
                    try { SpeechRecognition.resetTranscript(); } catch (e) { }
                } catch (e) { console.error('shadow accumulate error', e); }
            }
        } else if (!listening && transcript) {
            // Stopped — finalize any remaining transcript delta
            try {
                const current = transcript || '';
                const last = lastTranscriptRef.current || '';
                if (current.length > last.length) {
                    const delta = current.slice(last.length).trim();
                    if (delta) fullTranscriptRef.current = (fullTranscriptRef.current ? fullTranscriptRef.current + ' ' : '') + delta;
                }
            } catch (e) { console.error('finalize shadow transcript error', e); }
            lastTranscriptRef.current = '';
            // When not in shadow-reading mode (or drill), auto-run the check handler
            if (!shadowReadingRef.current && !drillRecording && !drillVideoRecording) {
                try { handleCheckShadow(); } catch (e) { console.error('auto check shadow error', e); }
            } else if (!shadowReadingRef.current && (drillRecording || drillVideoRecording)) {
                // no-op: drill recording flow handles scoring elsewhere
            } else {
                // If shadow-reading just finished (we set shadowReadingRef false before stop), run check
                try { handleCheckShadow(); } catch (e) { console.error('auto check shadow error', e); }
            }
        }
    }, [listening, transcript]);

    // Reset totalDuration when track or timestamps change, but preserve stored translations
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // Only fetch duration from actual media instead of resetting to 0 every time story/timestamps change.
        // This prevents the seeker from being disabled (max=0) during lesson transitions.
        // Preserve translations if lesson record already contains them for this track
        try {
            const expectedLen = Array.isArray(lyric) ? lyric.length : 0;
            const tRaw = dataLesson?.translations;
            const found = (tRaw && typeof tRaw === 'object') ? findTranslationsForTrack(tRaw, story, expectedLen) : null;
            if (found && expectedLen > 0) {
                setTranslations(sanitizeTranslationsArray(found));
            } else if (translations.length !== expectedLen) {
                setTranslations(Array.from({ length: expectedLen }, () => ''));
            }
        } catch (e) {
            setTranslations([]);
        }

        // Delay so audio element has time to mount
        const timer = setTimeout(() => {
            handleDurationUpdate();
        }, 500);
        return () => clearTimeout(timer);
    }, [story, timestamps]);

    // Fetch translations whenever lyric changes (always enabled)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        console.log('Translation useEffect triggered (always on):', {
            lyricLength: lyric?.length,
            translationsLength: translations.length,
            shouldFetch: lyric?.length > 0 && (translations.length === 0 || translations.length !== lyric.length)
        });
        if (lyric?.length > 0) {
            if (translations.length === 0 || translations.length !== lyric.length) {
                console.log('Fetching translations for', lyric.length, 'lines...');
                fetchTranslations();
            }
            // If translations missing in DB for this lesson track, request server to ensure them
            try {
                const trackKey = toLowerCamelCase(story);
                const lessonHasTranslations = dataLesson?.translations && (Array.isArray(dataLesson.translations) ? dataLesson.translations.length === lyric.length : (dataLesson.translations && Array.isArray(dataLesson.translations[trackKey]) && dataLesson.translations[trackKey].length === lyric.length));
                if (!lessonHasTranslations && dataLesson?.id) {
                    console.log('Requesting server to ensure translations for lesson', dataLesson.id);
                    if (skipEnsureOnThisPage) {
                        console.log('Skipping ensureOne because current path is Speaking');
                    } else if (isAdmin()) {
                        axios.post(`${process.env.REACT_APP_API_URL}/translation/ensureOne`, { lesson_id: dataLesson.id }, { timeout: 60000 })
                            .then(() => {
                                // re-fetch lesson to pick up saved translations
                                setTimeout(() => { lesson(); }, 800);
                            })
                            .catch(err => console.error('ensureOne error', err));
                    } else {
                        console.log('Skipping ensureOne: user not admin');
                    }
                }
            } catch (e) { console.error('ensure translations trigger error', e); }
        }
    }, [lyric]);

    // IPA is computed via `computedIpaData` (memoized) to avoid flicker/repeated work

    const fetchTranslations = async () => {
        if (!lyric || lyric.length === 0) {
            console.log('fetchTranslations: No lyric data');
            return;
        }
        // If lesson already has stored translations from backend, prefer those.
        // Backend stores translations as an object with track keys; detect and use current track.
        try {
            const t = dataLesson?.translations;
            const trackTrans = (t && typeof t === 'object') ? findTranslationsForTrack(t, story, lyric.length) : null;
            if (trackTrans) {
                setTranslations(sanitizeTranslationsArray(trackTrans));
                console.log('Using stored translations from lesson record for', story);
                return;
            }
        } catch (e) { console.error('check stored translations error', e); }

        console.log('fetchTranslations: Starting fetch for', lyric.length, 'lines');
        console.log('API URL:', `${process.env.REACT_APP_API_URL}/translation/batch`);

        setTranslationLoading(true);
        try {
            const payload = { lines: lyric, from: 'en', to: 'vi' };
            console.log('Translation request payload:', payload);

            const result = await axios.post(
                `${process.env.REACT_APP_API_URL}/translation/batch`,
                payload,
                { timeout: 30000 } // Increased timeout to 30s
            );

            console.log('Translation API response:', result?.data);

            if (result?.data?.translations && Array.isArray(result.data.translations) && result.data.translations.length === lyric.length) {
                const backendArr = sanitizeTranslationsArray(result.data.translations);
                const hasNonEmpty = backendArr.some(t => typeof t === 'string' && t.trim().length > 0);
                if (hasNonEmpty) {
                    setTranslations(backendArr);
                    console.log('✅ Translations loaded successfully:', backendArr.length, 'lines');
                    console.log('Sample translations:', backendArr.slice(0, 3));
                } else {
                    console.warn('⚠️ Backend returned translations array but entries are empty — requesting server to ensure translations');
                    setTranslations(Array(lyric.length).fill(''));
                    if (dataLesson?.id) {
                        if (skipEnsureOnThisPage) {
                            console.log('Skipping ensureOne after empty backend response because current path is Speaking');
                        } else if (isAdmin()) {
                            axios.post(`${process.env.REACT_APP_API_URL}/translation/ensureOne`, { lesson_id: dataLesson.id, force: true }, { timeout: 60000 })
                                .then(() => {
                                    message.info('Đã gửi yêu cầu dịch; làm mới trang sau vài giây để xem bản dịch.');
                                    setTimeout(() => lesson(), 1200);
                                })
                                .catch(err => {
                                    console.error('ensureOne after empty backend response error', err);
                                    message.warning('Không thể gửi yêu cầu dịch tự động');
                                });
                        } else {
                            console.log('Skipping ensureOne after empty backend response: user not admin');
                        }
                    }
                }
            } else {
                console.warn('⚠️ Invalid or incomplete translation response structure:', result?.data);
                // Backend returned invalid/incomplete response — request server to ensure translations instead of calling external API
                console.warn('⚠️ Invalid or incomplete translation response; requesting server to ensure translations');
                setTranslations(Array(lyric.length).fill(''));
                if (dataLesson?.id) {
                    if (skipEnsureOnThisPage) {
                        console.log('Skipping ensureOne after invalid backend response because current path is Speaking');
                    } else if (isAdmin()) {
                        axios.post(`${process.env.REACT_APP_API_URL}/translation/ensureOne`, { lesson_id: dataLesson.id }, { timeout: 60000 })
                            .then(() => {
                                message.info('Đã gửi yêu cầu dịch; làm mới trang sau vài giây để xem bản dịch.');
                                setTimeout(() => lesson(), 1200);
                            })
                            .catch(err => {
                                console.error('ensureOne after invalid backend response error', err);
                                message.warning('Không thể gửi yêu cầu dịch tự động');
                            });
                    } else {
                        console.log('Skipping ensureOne after invalid backend response: user not admin');
                    }
                }
            }
        } catch (e) {
            console.error('❌ Translation error:', e);
            console.error('Error details:', {
                message: e.message,
                response: e.response?.data,
                status: e.response?.status
            });
            setTranslations([]);

            if (e.code === 'ECONNABORTED') {
                message.error('Thời gian chờ quá lâu - Vui lòng thử lại');
            } else if (e.response?.status === 404) {
                message.error('API dịch không tìm thấy');
            } else {
                message.error('Lỗi kết nối API dịch - yêu cầu server đảm nhận dịch');
                setTranslations(Array(lyric.length).fill(''));
                if (dataLesson?.id) {
                    if (skipEnsureOnThisPage) {
                        console.log('Skipping ensureOne after translation error because current path is Speaking');
                    } else if (isAdmin()) {
                        axios.post(`${process.env.REACT_APP_API_URL}/translation/ensureOne`, { lesson_id: dataLesson.id }, { timeout: 60000 })
                            .then(() => {
                                message.info('Đã gửi yêu cầu dịch; làm mới trang sau vài giây để xem bản dịch.');
                                setTimeout(() => lesson(), 1200);
                            })
                            .catch(err => {
                                console.error('ensureOne after translation error', err);
                                message.warning('Không thể gửi yêu cầu dịch tự động');
                            });
                    } else {
                        console.log('Skipping ensureOne after translation error: user not admin');
                    }
                }
            }
        } finally {
            setTranslationLoading(false);
            console.log('fetchTranslations: Completed');
        }
    };

    // Try multiple key formats to locate per-track translations returned by backend
    const findTranslationsForTrack = (tRaw, storyName, expectedLength) => {
        if (!tRaw) return null;
        // If tRaw itself is a valid array, normalize to expectedLength
        if (Array.isArray(tRaw) && expectedLength) {
            const arr = tRaw.slice(0, expectedLength);
            while (arr.length < expectedLength) arr.push('');
            return arr;
        }
        const trackKey = toLowerCamelCase(storyName);
        const snakeFromCamel = trackKey.replace(/([A-Z])/g, '_$1').toLowerCase();
        const underscoredStory = String(storyName).replace(/\s+/g, '_').toLowerCase();
        const lowerKey = trackKey.toLowerCase();
        const variants = [trackKey, snakeFromCamel, underscoredStory, lowerKey, storyName];
        for (const k of variants) {
            if (!k) continue;
            if (Object.prototype.hasOwnProperty.call(tRaw, k)) {
                const val = tRaw[k];
                if (Array.isArray(val)) {
                    const arr = val.slice(0, expectedLength || val.length);
                    if (expectedLength) while (arr.length < expectedLength) arr.push('');
                    return arr;
                }
                if (typeof val === 'string') {
                    const arr = String(val).split('\n').map(s => s.trim());
                    const out = arr.slice(0, expectedLength || arr.length);
                    if (expectedLength) while (out.length < expectedLength) out.push('');
                    return out;
                }
            }
        }
        return null;
    };

    // Remove leading timestamp patterns like [00:00.00] from a translation line
    const sanitizeTranslationLine = (s) => {
        if (!s && s !== '') return '';
        try {
            let str = String(s || '');
            // remove one or more leading [mm:ss.xx] or [m:ss.xx] or [hh:mm:ss] patterns
            str = str.replace(/^\s*(?:\[\d{1,2}:\d{2}(?:[:.]\d+)?\]\s*)+/, '');
            return str;
        } catch (e) {
            return String(s || '');
        }
    };

    const sanitizeTranslationsArray = (arr) => {
        if (!Array.isArray(arr)) return arr;
        return arr.map(sanitizeTranslationLine);
    };

    // Lesson-level special UI flag detector (used instead of hardcoded slug checks)
    const lessonHasSpecialUi = () => {
        try {
            return !!(
                dataLesson?.specialUi || dataLesson?.special_ui ||
                dataLesson?.behaviorType === 'special'
            );
        } catch (e) { return false; }
    };

    // Use shared lightweight IPA converter to ensure consistent spacing and rules
    const textToIPA = (text) => {
        try {
            return textToIPAPure(text);
        } catch (e) {
            console.warn('textToIPAPure error, fallback to empty', e && e.message);
            return '';
        }
    };

    const handleDurationUpdate = () => {
        const m = getMediaElem();
        if (!m) return;
        const d = m.duration;
        if (d && Number.isFinite(d) && !isNaN(d) && d > 0) {
            setTotalDuration(d);
        } else {
            // Fallback: estimate from LRC timestamps
            const tsKey = toLowerCamelCase(storyRef.current || story);
            const tsArr = timestamps[tsKey] || [];
            if (tsArr.length > 0) {
                const lastTs = tsArr[tsArr.length - 1] / 1000;
                setTotalDuration(lastTs + 10); // add ~10s buffer
            }
        }
    };

    const handleLyric = (track) => {
        const startIndex = dataLesson[track]?.indexOf(']') + 1 || 0;
        const l = dataLesson[track]?.split('\n').map(el => el.slice(startIndex));
        setLyric(l);
    };

    const lesson = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/lesson/getBySlug/${slug}?_t=${Date.now()}`);
            let payload = response.data;
            if (Array.isArray(payload)) payload = payload[0];
            if (!payload) { setdataLesson({}); return; }

            const normalized = {
                id: payload.id || payload.lesson_id || null,
                title: payload.title || payload.name || payload.lesson_title || '',
                mainStory: payload.main_story || payload.mainStory || payload.main_story_text || payload.main || '',
                mainStoryAudio: payload.main_story_audio || payload.mainStoryAudio || payload.main_audio || payload.mainStoryUrl || payload.main_story_url || '',
                vocabulary: payload.vocabulary || payload.vocabulary_text || payload.vocabulary || '',
                vocabularyAudio: payload.vocabulary_audio || payload.vocabularyAudio || payload.vocabulary_audio_url || '',
                miniStory: payload.mini_story || payload.miniStory || payload.mini_story_text || '',
                miniStoryAudio: payload.mini_story_audio || payload.miniStoryAudio || payload.mini_story_audio_url || '',
                POV: payload.POV || payload.pov || payload.POV || payload.pov_text || '',
                POVAudio: payload.POV_audio || payload.POVAudio || payload.pov_audio || '',
                comment: payload.comment || payload.comments || payload.comment_text || '',
                commentAudio: payload.comment_audio || payload.commentAudio || '',
                translations: payload.translations || payload.translation_lines || payload.translation || payload.translations || [],
                _raw: payload,
            };

            // Normalize translations if backend returned JSON string, object with track keys, or newline text
            try {
                let t = normalized.translations;
                if (typeof t === 'string') {
                    try { t = JSON.parse(t); } catch (e) { t = String(t).split('\n').map(s => s.trim()); }
                }
                // If backend returned an object with per-track arrays, keep it as object
                if (Array.isArray(t) || (!t)) {
                    normalized.translations = Array.isArray(t) ? t : [];
                } else if (t && typeof t === 'object') {
                    normalized.translations = t; // object with track keys
                } else {
                    normalized.translations = [];
                }
            } catch (e) { normalized.translations = []; }

            // Initialize lyric/translations/ipa for current story so UI can render immediately
            try {
                const trackKey = toLowerCamelCase(story);
                const rawTrack = normalized[trackKey] || '';
                const startIndex = (rawTrack && rawTrack.indexOf(']') >= 0) ? rawTrack.indexOf(']') + 1 : 0;
                const l = rawTrack.split('\n').map(el => el.slice(startIndex));

                // If translations exist in lesson record, try finding per-track translations and set them immediately
                const tRaw = normalized.translations;
                const found = findTranslationsForTrack(tRaw, story, l.length);
                if (found) {
                    setTranslations(sanitizeTranslationsArray(found));
                    console.log('Using translations from lesson record for', story);
                } else {
                    setTranslations(Array.from({ length: l.length }, () => ''));
                }

                // Set lyric; IPA is derived later via memoization (computedIpaData)
                setLyric(l);
                // Finally set full lesson payload
                setdataLesson(normalized);
                // Try to fetch duration from the first available audio file immediately
                try {
                    const firstAudio = getFirstAudioUrl(normalized);
                    if (firstAudio) {
                        const proxied = proxyIfR2(firstAudio);
                        fetchFirstAudioDuration(proxied);
                    }
                } catch (e) { console.error('fetchFirstAudioDuration error', e); }
            } catch (e) {
                console.error('Init lyric/ipa/translations error', e);
                setdataLesson(normalized);
            }

        } catch (error) {
            console.error('Error fetching lesson by slug', slug, error);
            setdataLesson({});
        }
    };

    const userListening = async () => {
        const response = await getUserListening(cookies?.user?.token);
        setDataUserListening(response);
    };

    const onSubmit = async (values) => {
        if (values.text && values.text !== '') {
            await createUserListening({
                lesson_id: dataLesson.id, position: active,
                track: toLowerCamelCase(story), lyric: lyric[active], text: values.text,
            }, cookies?.user?.token);
            await userListening();
        }
        await stopMic();
    };

    const onPlaying = (ev) => {
        if (isSeekingRef.current) return;
        const media = getMediaElem();
        if (!media) return;
        const ct = media.currentTime;
        setcurrentTime(ct * 1000);
        // Update totalDuration if not yet set
        const d = media.duration;
        if (d && isFinite(d) && !isNaN(d) && d > 0) {
            setTotalDuration(prev => (prev !== d ? d : prev));
        }
        // If actual playback goes beyond metadata duration, extend totalDuration
        if (Number.isFinite(ct) && ct > 0) {
            setTotalDuration(prev => {
                if (!Number.isFinite(prev) || prev <= 0) return ct;
                if (ct <= prev + 0.5) return prev; // avoid frequent updates
                return ct;
            });
        }
        // Auto-advance active sentence in playAll mode
        if (playAll) {
            const tsArr = timestamps[toLowerCamelCase(story)] || [];
            if (tsArr.length > 0) {
                const currentIdx = active;
                const start = tsArr[currentIdx] / 1000;
                const end = tsArr[currentIdx + 1] !== undefined ? tsArr[currentIdx + 1] / 1000 : start + 10;
                const eps = 0.08;

                // 1. Handle repeating the current sentence
                if (loopMode === 1 && ct >= end - eps) {
                    const now = Date.now();
                    const lastRepeatTS = sentenceRepeatTSRef.current[currentIdx] || 0;
                    const currentRepeats = sentenceRepeatCountRef.current[currentIdx] || 0;

                    // Trigger repeat if below limit and not recently triggered (debounce)
                    if ((now - lastRepeatTS) > 800) {
                        if (sentenceRepeatLimit === 0 || currentRepeats < sentenceRepeatLimit) {
                            sentenceRepeatCountRef.current[currentIdx] = currentRepeats + 1;
                            sentenceRepeatTSRef.current[currentIdx] = now;
                            try { const m = getMediaElem(); if (m) m.currentTime = start; } catch (e) { }
                            return; // Stop here, repeat triggered
                        }
                    }
                }

        // 2. Otherwise, update active index based on current time
                let newActive = active;
                for (let i = 0; i < tsArr.length; i++) {
                    if (ct * 1000 >= tsArr[i]) {
                        newActive = i;
                    } else {
                        break;
                    }
                }

                if (newActive !== active) {
                    // Reset repeat count for the new sentence
                    sentenceRepeatCountRef.current[newActive] = 0;
                    setActive(newActive);
                }
            }
        }

        // Auto-reload check if very close to end (passive detection)
        if (Number.isFinite(media.duration) && media.duration > 0 && ct >= (media.duration - 0.3)) {
            triggerReloadOnEnd();
        }
    };

    const changeSpeedMode = (speed) => {
        setSpeedMode(speed);
        try { const m = getMediaElem(); if (m) m.playbackRate = speed; } catch (e) { }
    };

    const addTimestamps = async () => {
        const temp = {};
        const dataKeys = ['mainStory', 'miniStory', 'POV', 'comment', 'vocabulary'];
        for (const key of dataKeys) {
            if (dataLesson?.[key]) {
                const mapped = key === 'POV' ? 'pOV' : key;
                temp[mapped] = dataLesson[key].split('\n')
                    .map(el => convertTimeToMilliseconds(el.slice(1, el.indexOf(']'))));
            }
        }
        setTimestamps(temp);
        
        // Ensure duration is updated when timestamps are added (fallback source)
        setTimeout(() => handleDurationUpdate(), 100);
    };

    const safePlay = async () => {
        try {
            const media = getMediaElem();
            if (!media) return;
            if (!media.src && !media.currentSrc) return;

            // If media has ended, a full load() might be needed for some browsers to reset seeking
            if (media.ended) {
                try { 
                    media.load(); 
                    media.currentTime = 0; 
                } catch (e) {}
            }

            // Ensure duration is updated before playing
            handleDurationUpdate();

            // Ensure playback rate matches speed setting
            media.playbackRate = speedModeRef.current;
            await media.play();
        } catch (err) {
            console.error('safePlay failed', err);
            try { setisPlaying(false); } catch (e) { }
        }
    };

    const play = async i => {
        try {
            const ts = timestamps[toLowerCamelCase(story)];
            const media = getMediaElem();
            if (!media) return;
            media.currentTime = ts[i] / 1000;
            await safePlay();
            const endTime = ts[i + 1] / 1000 ? ts[i + 1] / 1000 : i + 1 === ts.length ? media.duration : 0;
            setDuration(endTime - ts[i] / 1000);
        } catch (e) { console.log(e); }
    };

    const pause = () => {
        try {
            safePlay().then(() => {
                try {
                    const m = getMediaElem(); if (m) { m.pause(); }
                    setisPlaying(false);
                    setPlayAll(false); setDuration(0); setCount(1); clearTimeout(timeoutPlay);
                    try { wholeReplayCountRef.current = 0; } catch (e) { }
                } catch (e) { }
            });
        } catch (e) { }
    };

    const PlayPause = async () => {
        if (!getAudioSrc() && !getVideoSrc()) { message.warning('Không có file âm thanh/ video cho bài này'); return; }
        if (isPlaying) {
            pause();
            setPlayAll(false);
        } else {
            // Resume playback from current position (don't reset after seek)
            try {
                const m = getMediaElem(); if (m) m.playbackRate = speedMode;

                // If user selected a different sentence via Prev/Next, ensure we start
                // playback from that sentence's start when currentTime is outside the
                // sentence bounds. This prevents Play from starting at the track's start.
                try {
                    const ts = timestamps[toLowerCamelCase(story)] || [];
                    if (ts && ts.length > 0 && m) {
                        const start = (ts[active] || 0) / 1000;
                        const end = (ts[active + 1] || (start + 10)) / 1000;
                        const ct = m.currentTime || 0;
                        const eps = 0.08;
                        if (ct < start - eps || ct >= end - eps) {
                            try { m.currentTime = start; } catch (e) { }
                        }
                    }
                } catch (e) { console.warn('Ensure start time check failed', e); }

                // If global transcript is OFF, and user previously revealed some lines via eye icon,
                // hide them again when starting playback.
                if (!showTranscript) {
                    setHiddenLines(prev => {
                        const next = { ...(prev || {}) };
                        let changed = false;
                        for (const k of Object.keys(next)) {
                            if (next[k] === false) { next[k] = true; changed = true; }
                        }
                        // also hide current active line if it was revealed but not yet in map
                        if (next[active] === false) { next[active] = true; changed = true; }
                        if (!changed) return prev;
                        try { sessionStorage.setItem(`hiddenLines_${slug}`, JSON.stringify(next)); } catch (err) { }
                        return next;
                    });
                }
                // reset per-sentence repeat markers when starting playAll
                sentenceRepeatCountRef.current = {};
                // reset whole-track replay counter
                try { wholeReplayCountRef.current = 0; } catch (e) { }
                setPlayAll(true);
                await safePlay();
            } catch (e) { console.log(e); }
            setisPlaying(true);
        }
    };

    const handleChangeStory = (e) => { setStory(e); setActive(0); setisPlaying(false); };

    const clearAutoAdvanceLS = () => {
        if (autoAdvanceLSTimerRef.current) {
            clearTimeout(autoAdvanceLSTimerRef.current);
            autoAdvanceLSTimerRef.current = null;
        }
    };

    /** Play just a single sentence (seek to its timestamp, stop at next timestamp or +10s) */
    const playSentenceOnly = async (i) => {
        const tsArr = timestamps[toLowerCamelCase(story)] || [];
        if (!tsArr.length) { message.warning('Track này chưa có timestamp.'); return; }
        const media = getMediaElem();
        if (!media) { message.warning('Không có file âm thanh/video.'); return; }

        // Cancel any previous per-sentence playback interval
        if (playSentenceIntervalRef.current) {
            clearInterval(playSentenceIntervalRef.current);
            playSentenceIntervalRef.current = null;
        }

        const start = tsArr[i] / 1000;
        const end = tsArr[i + 1] !== undefined ? tsArr[i + 1] / 1000 : start + 10;

        // If not in per-sentence video mode, just seek and continue full playback
        if (!perSentenceVideoMode) {
            setActive(i);
            setPlayingSentence(null);
            media.currentTime = start;
            media.playbackRate = speedModeRef.current;
            setcurrentTime(start * 1000);
            setisPlaying(true);
            setPlayAll(true);
            await safePlay();
            return;
        }

        // Stop full playback if running (only for special per-sentence modes like video recording)
        if (!media.paused) {
            media.pause();
            setisPlaying(false);
            setPlayAll(false);
        }

        setActive(i);
        setPlayingSentence(i);

        media.currentTime = start;
        media.playbackRate = speedModeRef.current;
        setcurrentTime(start * 1000);
        // If per-sentence video mode is enabled, start recorder for this sentence
        if (perSentenceVideoMode && perSentenceCameraStreamRef.current) {
            try {
                const vStream = perSentenceCameraStreamRef.current;
                const mimeTypes = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
                let sel = '';
                for (const m of mimeTypes) { if (MediaRecorder.isTypeSupported(m)) { sel = m; break; } }
                const rec = new MediaRecorder(vStream, sel ? { mimeType: sel } : {});
                const chunks = [];
                rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
                rec.onstop = () => {
                    try {
                        const blob = new Blob(chunks, { type: sel || 'video/webm' });
                        const ext = sel && sel.includes('mp4') ? 'mp4' : 'webm';
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = `video_sentence_${i}_${(dataLesson?.title || 'lesson').replace(/\s+/g, '_')}.${ext}`;
                        a.click(); URL.revokeObjectURL(url);
                        message.success(`Đã lưu video câu ${i + 1}`);
                    } catch (e) { console.error('per-sentence video save error', e); }
                };
                try { rec.start(); perSentenceVideoRecorderRef.current = rec; } catch (e) { console.warn('start per-sentence recorder failed', e); }
            } catch (e) { console.warn('per-sentence recorder setup failed', e); }
        }
        try {
            // mark this sentence as not-yet-repeated for manual per-sentence play
            sentenceRepeatCountRef.current[i] = 0;
            await media.play();
        } catch (e) {
            setPlayingSentence(null);
            return;
        }

        // Poll to stop at end time; clear on unmount via ref
        playSentenceIntervalRef.current = setInterval(() => {
            if (!media || media.paused) {
                clearInterval(playSentenceIntervalRef.current);
                playSentenceIntervalRef.current = null;
                setPlayingSentence(null);
                return;
            }
            if (media.currentTime >= end) {
                // stop per-sentence recorder if active
                try {
                    if (perSentenceVideoMode && perSentenceVideoRecorderRef.current) {
                        try { perSentenceVideoRecorderRef.current.stop(); } catch (e) { console.warn('stop per-sentence recorder', e); }
                        perSentenceVideoRecorderRef.current = null;
                    }
                } catch (e) { }

                try { media.pause(); } catch (e) { }
                clearInterval(playSentenceIntervalRef.current);
                playSentenceIntervalRef.current = null;
                // Loop single sentence N times if configured for manual per-sentence play
                if (loopMode === 1) {
                    const now = Date.now();
                    const last = sentenceRepeatTSRef.current[i] || 0;
                    const dur = Math.max(0, end - start);
                    const currentRepeats = sentenceRepeatCountRef.current[i] || 0;

                    // Trigger repeat if below limit and not recently triggered (debounce)
                    if (dur >= 0.5 && (now - last) > 800) {
                        if (sentenceRepeatLimit === 0 || currentRepeats < sentenceRepeatLimit) {
                            sentenceRepeatCountRef.current[i] = currentRepeats + 1;
                            sentenceRepeatTSRef.current[i] = now;
                            try { media.currentTime = start; } catch (e) { }
                            return;
                        }
                    }
                }
                setPlayingSentence(null);
            }
        }, 80);
    };

    /** ── Drill Mode helpers ──────────────────────────────────────────────── */
    const clearDrillInterval = () => {
        if (drillIntervalRef.current) {
            clearInterval(drillIntervalRef.current);
            drillIntervalRef.current = null;
        }
    };

    /** Start the stop-at-end interval using the already-set drillEndRef */
    const startDrillInterval = () => {
        clearDrillInterval();
        drillIntervalRef.current = setInterval(async () => {
            const media = getMediaElem();
            if (!media || media.paused) {
                setDrillIsPlaying(false);
                clearDrillInterval(); return;
            }
            if (media.currentTime >= drillEndRef.current) {
                // Stop per-sentence video recorder if active
                try {
                    if (perSentenceVideoMode && perSentenceVideoRecorderRef.current) {
                        try { perSentenceVideoRecorderRef.current.stop(); } catch (e) { console.warn('stop per-sentence recorder', e); }
                        perSentenceVideoRecorderRef.current = null;
                    }
                } catch (e) { }

                try { media.pause(); } catch (e) { }
                clearDrillInterval();

                const doneCount = drillRepeatCountRef.current + 1;
                // Use <= to ensure "Repeat N times" means N repetitions after the first play
                if (doneCount <= drillRepeatRef.current) {
                    // Repeat the same sentence
                    drillRepeatCountRef.current = doneCount;
                    const tsArr = timestamps[toLowerCamelCase(story)] || [];
                    const idx = drillIndexRef.current;
                    const start = tsArr[idx] !== undefined ? tsArr[idx] / 1000 : 0;
                    setTimeout(async () => {
                        if (!audioElem.current) return;
                        audioElem.current.currentTime = start;
                        audioElem.current.playbackRate = speedModeRef.current;
                        try { await audioElem.current.play(); } catch (e) { setDrillIsPlaying(false); return; }
                        setDrillIsPlaying(true);
                        startDrillInterval();
                    }, 350);
                } else {
                    // All repeats done
                    drillRepeatCountRef.current = 0;
                    setDrillIsPlaying(false);
                    if (drillAutoNextRef.current) {
                        const nextIdx = drillIndexRef.current + 1;
                        const tsArr = timestamps[toLowerCamelCase(story)] || [];
                        if (nextIdx < lyricRef.current?.length && tsArr[nextIdx] !== undefined) {
                            setTimeout(() => playDrillSentence(nextIdx), 500);
                        }
                    }
                }
            }
        }, 80);
    };

    /** Play the current drill sentence from the beginning and stop at the next timestamp */
    const playDrillSentence = async (idx) => {
        clearDrillInterval();
        const tsArr = timestamps[toLowerCamelCase(story)] || [];
        if (!tsArr.length || !audioElem.current) return;
        const media = getMediaElem();
        if (!media) return;
        drillIndexRef.current = idx;
        setDrillIndex(idx);
        setDrillAnswer('');
        setDrillAnswerResult(null);
        drillRepeatCountRef.current = 0;
        const start = tsArr[idx] / 1000;
        const end = tsArr[idx + 1] !== undefined ? tsArr[idx + 1] / 1000 : start + 10;
        drillEndRef.current = end;
        media.currentTime = start;
        media.playbackRate = speedModeRef.current;
        // If per-sentence video mode is enabled, start a short MediaRecorder for the camera stream
        if (perSentenceVideoMode && perSentenceCameraStreamRef.current) {
            try {
                const vStream = perSentenceCameraStreamRef.current;
                const mimeTypes = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
                let sel = '';
                for (const m of mimeTypes) { if (MediaRecorder.isTypeSupported(m)) { sel = m; break; } }
                const rec = new MediaRecorder(vStream, sel ? { mimeType: sel } : {});
                const chunks = [];
                rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
                rec.onstop = () => {
                    try {
                        const blob = new Blob(chunks, { type: sel || 'video/webm' });
                        const ext = sel && sel.includes('mp4') ? 'mp4' : 'webm';
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = `video_sentence_${idx}_${(dataLesson?.title || 'lesson').replace(/\s+/g, '_')}.${ext}`;
                        a.click(); URL.revokeObjectURL(url);
                        message.success(`Đã lưu video câu ${idx + 1}`);
                    } catch (e) { console.error('per-sentence video save error', e); }
                };
                try { rec.start(); perSentenceVideoRecorderRef.current = rec; } catch (e) { console.warn('start per-sentence recorder failed', e); }
            } catch (e) { console.warn('per-sentence recorder setup failed', e); }
        }
        try { await media.play(); } catch (e) { return; }
        setDrillIsPlaying(true);
        startDrillInterval();
    };

    const openDrillMode = () => {
        // Stop any ongoing playback
        if (!audioElem.current?.paused) { audioElem.current.pause(); setisPlaying(false); setPlayAll(false); }
        setDrillIndex(0);
        setShowDrillMode(true);
        // Auto-play first sentence after brief delay
        setTimeout(() => playDrillSentence(0), 200);
    };

    const closeDrillMode = () => {
        clearDrillInterval();
        if (!audioElem.current?.paused) { audioElem.current.pause(); }
        setDrillIsPlaying(false);
        setShowDrillMode(false);
        setDrillAnswer('');
        setDrillAnswerResult(null);
    };

    const checkDrillAnswer = () => {
        const correct = lyric?.[drillIndex] || '';
        if (!drillAnswer.trim() || !correct) return;
        const clean = s => s.replace(/[!@#$%^&*(),.?"":{}|<>""''`';:]/g, '').toLowerCase().trim();
        const correctWords = correct.split(/\s+/).filter(Boolean);
        const answerWords = drillAnswer.trim().split(/\s+/).filter(Boolean);
        const answerSet = new Set(answerWords.map(clean));
        let matches = 0;
        const words = correctWords.map(w => {
            const matched = answerSet.has(clean(w));
            if (matched) matches++;
            return { word: w, matched };
        });
        const point = Math.round((matches / (correctWords.length || 1)) * 100);
        setDrillAnswerResult({ point, words });
    };

    /**
     * Timer-based lyric auto-advance (used when no audio is available).
     * Each sentence stays on screen for (wordCount / (2.3 * speed)) seconds.
     */
    const WORDS_PER_SEC_LS = 2.3;
    const MIN_MS_LS = 1200;
    const scheduleAutoAdvanceLS = (startIdx) => {
        clearAutoAdvanceLS();
        const advanceTo = (idx) => {
            const lines = lyricRef.current;
            if (!lines || idx >= lines.length) return;
            setActive(idx);
            const wordCount = (lines[idx] || '').split(/\s+/).filter(Boolean).length || 1;
            const speed = speedModeRef.current;
            const ms = Math.max(MIN_MS_LS, (wordCount / (WORDS_PER_SEC_LS * speed)) * 1000);
            autoAdvanceLSTimerRef.current = setTimeout(() => {
                if (!shadowReadingRef.current) return;
                advanceTo(idx + 1);
            }, ms);
        };
        advanceTo(startIdx);
    };

    /**
     * "Luyện nói": ghi âm continuous xuyên suốt, lyric tự chạy theo timer (speedMode).
     * Không phát audio. Khi Dừng → batch score tất cả câu.
     */
    const clickMic = async () => {
        try { SpeechRecognition.getRecognition().abort(); } catch (e) { }
        try { SpeechRecognition.resetTranscript(); } catch (e) { }
        fullTranscriptRef.current = '';
        shadowReadingRef.current = true;
        setScoreResult(null); // clear previous results
        // Use timer-based lyric advance (same as Speaking.js) — no audio playback
        setActive(0);
        scheduleAutoAdvanceLS(0);
        await SpeechRecognition.startListening({ language: 'en-US', continuous: true });
    };

    const stopMic = async () => {
        shadowReadingRef.current = false;
        clearAutoAdvanceLS();
        await SpeechRecognition.stopListening();
        // batchScoreShadow triggered from listening useEffect
    };

    const scoreTextLS = (sentenceWords, resultText) => {
        const resultWords = resultText
            .replace(/[!@#$%^&*(),.?"":{}|<>""]/g, '')
            .toLowerCase().split(/\s+/);
        let matches = 0;
        sentenceWords.forEach(w => {
            const clean = w.replace(/[!@#$%^&*(),.?"":{}|<>""]/g, '').toLowerCase();
            if (resultWords.includes(clean)) matches++;
        });
        return Math.round((matches / (sentenceWords.length || 1)) * 100);
    };

    const batchScoreShadow = async (fullText) => {
        if (!fullText?.trim() || !lyric?.length) return;
        setIsBatchScoring(true);
        try {
            // Align transcript to each lyric line (greedy sequence alignment)
            const aligned = alignTranscript(fullText, lyric);

            const lines = lyric.map((line, pos) => {
                const rawPoint = (aligned[pos] && aligned[pos].point) || 0;
                let p = Math.round(rawPoint);
                if (p < 25) p = 0;
                return {
                    lyric: line,
                    pos,
                    point: p,
                    words: aligned[pos]?.words || [],
                    spokenLine: aligned[pos]?.spokenLine || [],
                };
            });

            const avgPoint = Math.round(
                lines.reduce((s, l) => s + l.point, 0) / (lines.length || 1)
            );

            // POST to server — send the aligned chunk for each sentence
            const posts = lyric.map((line, pos) =>
                createUserListening({
                    lesson_id: dataLesson.id, position: pos,
                    track: toLowerCamelCase(story),
                    lyric: line || '',
                    text: aligned[pos].chunk.join(' ') || fullText,
                }, cookies?.user?.token)
            );
            await Promise.all(posts);
            await userListening();

            // Show results modal (skip for lessons with specialUi)
            setScoreResult({ avgPoint, spokenText: fullText, lines, totalLines: lyric.length });
            if (!lessonHasSpecialUi()) setShowScoreModal(true);
        } catch (e) {
            console.error('Batch score error', e);
            message.error('Có lỗi khi chấm điểm.');
        } finally {
            setIsBatchScoring(false);
            try { SpeechRecognition.resetTranscript(); } catch (e) { }
        }
    };

    const handleCheckShadow = async () => {
        const spoken = (fullTranscriptRef.current || '').trim();
        if (!spoken) { message.warning('Không có transcript để chấm'); return; }
        try {
            setIsBatchScoring(true);
            await batchScoreShadow(spoken);
        } catch (e) {
            console.error('Error scoring manual transcript', e);
            message.error('Không thể chấm transcript. Thử lại.');
        } finally {
            setIsBatchScoring(false);
        }
    };



    const downloadAudio = () => {
        const src = getAudioSrc();
        if (!src) { message.error('Không tìm thấy file âm thanh!'); return; }
        const link = document.createElement('a');
        link.href = src;
        link.download = `${dataLesson?.title || 'audio'}_${story.replace(/\s/g, '_')}.mp3`;
        link.target = '_blank';
        link.click();
    };

    // Drill mode audio recording with word highlighting
    const startDrillAudioRecording = async () => {
        try {
            // Reset to beginning
            setDrillIndex(0);
            setDrillAnswer('');
            setDrillAnswerResult(null);
            resetDrillTranscript();

            // Start speech recognition first so initial words are captured
            try { await SpeechRecognition.startListening({ language: 'en-US', continuous: true }); } catch (e) { console.warn('SpeechRecognition start failed', e); }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeTypes = ['audio/webm', 'audio/mp4', 'audio/ogg'];
            let selectedMime = '';
            for (const mime of mimeTypes) {
                if (MediaRecorder.isTypeSupported(mime)) { selectedMime = mime; break; }
            }
            const recorderOptions = selectedMime ? { mimeType: selectedMime } : {};
            const recorder = new MediaRecorder(stream, recorderOptions);
            const chunks = [];
            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
            recorder.onstop = () => {
                const ext = selectedMime.includes('mp4') ? 'm4a' : selectedMime.includes('ogg') ? 'ogg' : 'webm';
                const blob = new Blob(chunks, { type: selectedMime || 'audio/webm' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `recording_audio_${dataLesson?.title || 'lesson'}.${ext}`;
                a.click(); URL.revokeObjectURL(url);
                message.success('Đã lưu bản ghi âm!');
                stream.getTracks().forEach(t => t.stop());
            };
            recorder.start();
            setDrillMediaRecorder(recorder);
            setDrillRecording(true);
            // SpeechRecognition already started; mark listening state
            setDrillListening(true);
            // Play from beginning
            setTimeout(() => playDrillSentence(0), 200);
            message.success('Đang ghi âm và theo dõi lời nói...');
        } catch (err) {
            console.error('Audio recording error:', err);
            if (err.name === 'NotAllowedError') {
                message.error('Bạn đã từ chối quyền truy cập microphone.');
            } else {
                message.error('Không thể bắt đầu ghi âm: ' + (err.message || err.name));
            }
        }
    };

    const stopDrillAudioRecording = async () => {
        if (drillMediaRecorder && drillRecording) {
            drillMediaRecorder.stop();
            setDrillRecording(false);
            setDrillMediaRecorder(null);
            // Stop speech recognition
            try { await SpeechRecognition.stopListening(); } catch (e) { try { SpeechRecognition.abortListening && SpeechRecognition.abortListening(); } catch (_) { } }
            setDrillListening(false);

            // Wait briefly for recognition to finalize
            await new Promise(r => setTimeout(r, 300));

            const spoken = (drillRecordingTranscript || transcript || fullTranscriptRef.current || '').trim();
            if (spoken) {
                // If user is logged in, batch-score and save results. Otherwise show recording modal only.
                if (cookies?.user) {
                    try {
                        setIsBatchScoring(true);
                        await batchScoreShadow(spoken);
                    } catch (e) {
                        console.error('Error scoring recording:', e);
                        message.error('Không thể chấm điểm bản ghi âm.');
                    } finally {
                        setIsBatchScoring(false);
                    }
                } else {
                    // guest: show transcript modal without saving
                    setShowDrillRecordingModal(true);
                }
            }
            else {
                message.warning('Không có transcript để chấm. Vui lòng bật nhận dạng giọng nói khi ghi âm.');
            }
        }
    };

    // Drill mode video recording - doesn't affect audio playback
    const startDrillVideoRecording = async () => {
        try {
            // Reset transcript
            setDrillRecordingTranscript('');

            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: true
            });
            drillCameraStreamRef.current = stream;
            if (drillCameraPreviewRef.current) {
                drillCameraPreviewRef.current.srcObject = stream;
                // Ensure video plays
                try {
                    await drillCameraPreviewRef.current.play();
                } catch (playErr) {
                    console.log('Video autoplay handled by browser');
                }
            }
            const mimeTypes = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
            let selectedMime = '';
            for (const mime of mimeTypes) {
                if (MediaRecorder.isTypeSupported(mime)) { selectedMime = mime; break; }
            }
            const recorderOptions = selectedMime ? { mimeType: selectedMime } : {};
            const recorder = new MediaRecorder(stream, recorderOptions);
            const chunks = [];
            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
            recorder.onstop = () => {
                const ext = selectedMime.includes('mp4') ? 'mp4' : 'webm';
                const blob = new Blob(chunks, { type: selectedMime || 'video/webm' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `recording_video_${dataLesson?.title || 'lesson'}.${ext}`;
                a.click(); URL.revokeObjectURL(url);
                message.success('Đã lưu bản ghi hình!');
                stream.getTracks().forEach(t => t.stop());
                if (drillCameraPreviewRef.current) drillCameraPreviewRef.current.srcObject = null;
                drillCameraStreamRef.current = null;
            };
            recorder.start();
            setDrillMediaRecorder(recorder);
            setDrillVideoRecording(true);

            // Start speech recognition for word highlighting
            if (browserSupportsSpeechRecognition) {
                try {
                    await SpeechRecognition.startListening({ language: 'en-US', continuous: true });
                    setDrillListening(true);
                    console.log('Speech recognition started successfully');
                } catch (speechErr) {
                    console.error('Speech recognition error:', speechErr);
                    message.warning('Ghi hình đã bắt đầu nhưng không thể bật nhận dạng giọng nói');
                }
            } else {
                console.warn('Browser does not support speech recognition');
                message.warning('Trình duyệt không hỗ trợ nhận dạng giọng nói - chỉ ghi hình và âm thanh');
            }

            message.success('Đang ghi hình và thu âm!');
        } catch (err) {
            console.error('Video recording error:', err);
            if (err.name === 'NotAllowedError') {
                message.error('Bạn đã từ chối quyền truy cập camera/microphone.');
            } else if (err.name === 'NotFoundError') {
                message.error('Không tìm thấy camera.');
            } else {
                message.error('Không thể bắt đầu ghi hình: ' + (err.message || err.name));
            }
        }
    };



    const togglePerSentenceVideoMode = async (enable) => {
        if (enable) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }, audio: true });
                perSentenceCameraStreamRef.current = stream;
                if (drillCameraPreviewRef.current) {
                    drillCameraPreviewRef.current.srcObject = stream;
                    try { await drillCameraPreviewRef.current.play(); } catch (e) { /* ignore */ }
                }
                setPerSentenceVideoMode(true);
                message.success('Bật chế độ ghi hình từng câu');
            } catch (e) {
                console.error('Could not get camera for per-sentence mode', e);
                message.error('Không thể bật camera');
                setPerSentenceVideoMode(false);
            }
        } else {
            try {
                if (perSentenceCameraStreamRef.current) {
                    perSentenceCameraStreamRef.current.getTracks().forEach(t => t.stop());
                    perSentenceCameraStreamRef.current = null;
                }
            } catch (e) { }
            setPerSentenceVideoMode(false);
            message.info('Tắt chế độ ghi hình từng câu');
        }
    };

    const stopDrillVideoRecording = () => {
        if (drillMediaRecorder && drillVideoRecording) {
            message.info('Đang dừng và lưu bản ghi hình...');
            drillMediaRecorder.stop();
            setDrillVideoRecording(false);
            setDrillMediaRecorder(null);
            // Stop speech recognition
            SpeechRecognition.stopListening();
            setDrillListening(false);
            // Show recording summary modal
            setTimeout(() => {
                setShowDrillRecordingModal(true);
            }, 500);
        }
    };

    // Close drill camera without recording
    const closeDrillCamera = () => {
        if (drillCameraStreamRef.current) {
            drillCameraStreamRef.current.getTracks().forEach(t => t.stop());
            if (drillCameraPreviewRef.current) drillCameraPreviewRef.current.srcObject = null;
            drillCameraStreamRef.current = null;
        }
        setDrillVideoRecording(false);
        setDrillMediaRecorder(null);
        message.info('Đã đóng camera.');
    };

    const ts = timestamps[toLowerCamelCase(story)] || [];
    const audioSrc = getAudioSrc();

    useEffect(() => {
        try {
            console.debug('Listening audioSrc:', audioSrc);
            if (audioElem.current) {
                // Ensure crossOrigin set before assigning/using src for CORS requests
                try { audioElem.current.crossOrigin = 'anonymous'; } catch (e) { }
            }
        } catch (e) { console.error('audioSrc debug error', e); }
    }, [audioSrc]);

    // Prime IPA computation for first visible lines and nearby lines
    useEffect(() => {
        if (!showIPA) return;
        const initial = [];
        for (let i = 0; i < Math.min(5, (lyric || []).length); i++) initial.push(i);
        // also compute a sliding window around active
        const window = [];
        for (let j = Math.max(0, active - 2); j <= Math.min((lyric || []).length - 1, active + 2); j++) window.push(j);
        scheduleComputeIpa([...new Set([...initial, ...window])]);
    }, [lyric, active, showIPA]);

    // Compute actual practice counts/hours for this lesson from userListening data
    const actualSessions = (dataUserListening || []).filter(item => +item.lesson_id === +dataLesson?.id).length;
    const lessonDurationHours = (totalDuration || 0) / 3600; // seconds -> hours
    const actualHours = Math.round((lessonDurationHours * actualSessions) * 100) / 100;
    const daysPercent = goalDays > 0 ? Math.min(100, Math.round((actualSessions / goalDays) * 100)) : 0;
    const hoursPercent = goalHours > 0 ? Math.min(100, Math.round((actualHours / goalHours) * 100)) : 0;

    const progressMaxSec = (() => {
        const lastTsSec = ts.length ? (ts[ts.length - 1] / 1000) : 0;
        const mediaSec = Number.isFinite(totalDuration) ? totalDuration : 0;
        const bufferSec = 4;
        const lastTsWithBuffer = lastTsSec > 0 ? (lastTsSec + bufferSec) : 0;

        if (lastTsSec > 0 && mediaSec > 0) {
            const diff = mediaSec - lastTsSec;
            // If the gap is small (<= buffer), trust actual media duration.
            if (diff <= bufferSec) return mediaSec;
            // If the gap is large, cap the tail to reduce excess.
            return Math.min(mediaSec, lastTsWithBuffer);
        }
        if (mediaSec > 0) return mediaSec;
        if (lastTsWithBuffer > 0) return lastTsWithBuffer;
        return 0;
    })();
    const progressValueSec = Math.min(currentTime / 1000, progressMaxSec || 0);

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#0c1929] to-[#162d50]">
            {/* Hidden Audio */}
            {getVideoSrc() && (
                <video src={getVideoSrc()} ref={videoElem} crossOrigin="anonymous" onError={(e) => { console.error('Video element load/play error', e); message.error('Không thể tải video'); }} onTimeUpdate={onPlaying}
                    preload="metadata"
                    onLoadedMetadata={handleDurationUpdate}
                    onDurationChange={handleDurationUpdate}
                    onCanPlay={handleDurationUpdate}
                    onEnded={() => {
                        const media = getMediaElem();
                        try {
                            // Auto-repeat whole track according to `replay` setting
                            if (replay && replay > 1) {
                                if (wholeReplayCountRef.current < (replay - 1)) {
                                    wholeReplayCountRef.current += 1;
                                    try { if (timeoutPlay) { clearTimeout(timeoutPlay); setTimeoutPlay(null); } } catch (e) { }
                                    const id = setTimeout(() => { try { media.currentTime = 0; media.play(); setisPlaying(true); } catch (e) { } }, (timeReplay || 0) * 1000);
                                    setTimeoutPlay(id);
                                    return;
                                } else {
                                    wholeReplayCountRef.current = 0;
                                }
                            }
                        } catch (e) { console.warn('replay onEnded error', e); }
                        if (repeatWholeTrack && playAll) {
                            try { media.currentTime = 0; media.play(); setisPlaying(true); } catch (e) { setisPlaying(false); setPlayAll(false); }
                        } else {
                            try {
                                if (media && Number.isFinite(media.duration) && media.duration > 0) {
                                    media.currentTime = media.duration;
                                    setcurrentTime(media.duration * 1000);
                                    const finalSec = Number.isFinite(media.currentTime) ? media.currentTime : media.duration;
                                    setTotalDuration(Math.max(media.duration, finalSec));
                                }
                            } catch (e) { }
                            setisPlaying(false); setPlayAll(false);
                            triggerReloadOnEnd();
                        }
                    }}
                    onSeeked={() => {
                        const m = getMediaElem();
                        if (m) {
                            setcurrentTime(m.currentTime * 1000);
                            if (Number.isFinite(m.duration) && m.duration > 0 && m.currentTime >= (m.duration - 0.3)) {
                                triggerReloadOnEnd();
                            }
                        }
                    }}
                    style={{ width: '100%', maxHeight: 420, borderRadius: 8 }}
                    controls
                />
            )}

            {/* Hidden Audio (fallback when no video) */}
            {audioSrc && (
                <audio src={audioSrc} ref={audioElem} crossOrigin="anonymous" onError={(e) => { console.error('Audio element load/play error', e); message.error('Không thể tải file âm thanh'); }} onTimeUpdate={onPlaying}
                    preload="metadata"
                    onLoadedMetadata={handleDurationUpdate}
                    onDurationChange={handleDurationUpdate}
                    onCanPlay={handleDurationUpdate}
                    onEnded={() => {
                        const media = getMediaElem();
                        try {
                            // Auto-repeat whole track according to `replay` setting
                            if (replay && replay > 1) {
                                if (wholeReplayCountRef.current < (replay - 1)) {
                                    wholeReplayCountRef.current += 1;
                                    try { if (timeoutPlay) { clearTimeout(timeoutPlay); setTimeoutPlay(null); } } catch (e) { }
                                    const id = setTimeout(() => { try { media.currentTime = 0; media.play(); setisPlaying(true); } catch (e) { } }, (timeReplay || 0) * 1000);
                                    setTimeoutPlay(id);
                                    return;
                                } else {
                                    wholeReplayCountRef.current = 0;
                                }
                            }
                        } catch (e) { console.warn('replay onEnded error', e); }
                        if (repeatWholeTrack && playAll) {
                            try { media.currentTime = 0; media.play(); setisPlaying(true); } catch (e) { setisPlaying(false); setPlayAll(false); }
                        } else {
                            try {
                                if (media && Number.isFinite(media.duration) && media.duration > 0) {
                                    media.currentTime = media.duration;
                                    setcurrentTime(media.duration * 1000);
                                    const finalSec = Number.isFinite(media.currentTime) ? media.currentTime : media.duration;
                                    setTotalDuration(Math.max(media.duration, finalSec));
                                }
                            } catch (e) { }
                            setisPlaying(false); setPlayAll(false);
                            triggerReloadOnEnd();
                        }
                    }}
                    onSeeked={() => {
                        const m = getMediaElem();
                        if (m) {
                            setcurrentTime(m.currentTime * 1000);
                            if (Number.isFinite(m.duration) && m.duration > 0 && m.currentTime >= (m.duration - 0.3)) {
                                triggerReloadOnEnd();
                            }
                        }
                    }} />
            )}

            {/* Header Bar */}
            <div className="bg-[#0a1628]/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-20">
                <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Breadcrumb
                        className="[&_.ant-breadcrumb-link]:text-blue-300 [&_.ant-breadcrumb-separator]:text-blue-400 [&_a]:text-blue-300 [&_a:hover]:text-white"
                        items={[
                            { title: <Link to="/"><HomeOutlined /> Trang chủ</Link> },
                            { title: <Link to="/topic">Topic</Link> },
                            { title: <Link to={"/lesson/" + dataLesson?.course_slug}>{dataLesson?.course_title}</Link> },
                            { title: <span className="text-white">{dataLesson?.title}</span> },
                        ]}
                    />
                </div>
            </div>

            <div className="max-w-screen-xl mx-auto px-4 py-6 pb-[140px]">



                {/* ── Drill Mode: inline panel ───────────────────────────────── */}
                {showDrillMode && (() => {
                    const validLyric = lyric?.filter(l => l?.trim()) || [];
                    const validIdx = validLyric.indexOf(lyric?.[drillIndex] || '');
                    const pct = validLyric.length ? Math.round(((validIdx + 1) / validLyric.length) * 100) : 0;
                    return (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Main Content - 2 columns */}
                            <div className="lg:col-span-2 space-y-4">
                                {/* Header row */}
                                <div className="w-full flex items-center justify-between">
                                    <button
                                        onClick={closeDrillMode}
                                        className="flex items-center gap-2 text-blue-300 hover:text-white transition-colors text-sm font-medium"
                                    >
                                        <StepBackwardOutlined /> Quay lại
                                    </button>
                                    <p className="text-gray-400 text-sm tracking-wide">
                                        📖 <span className="text-blue-300">{story}</span>
                                        &nbsp;·&nbsp; Câu <span className="text-white font-bold">{validIdx + 1}</span> / {validLyric.length}
                                    </p>
                                    <button
                                        onClick={() => setShowDrillSettings(v => !v)}
                                        className="flex items-center gap-1 text-gray-400 hover:text-white text-sm transition-colors lg:hidden"
                                    >
                                        <SettingOutlined /> Cài đặt
                                    </button>
                                </div>

                                {/* Progress bar using Ant Design Progress */}
                                <div className="w-full mb-4">
                                    <Progress
                                        percent={pct}
                                        showInfo={false}
                                        strokeColor="#22d3ee"
                                        trailColor="rgba(255,255,255,0.08)"
                                        strokeLinecap="round"
                                        className="[&_.ant-progress-inner]:!bg-white/5"
                                    />
                                </div>

                                {/* Per-sentence audio progress bar */}
                                {(() => {
                                    const tsArr = timestamps[toLowerCamelCase(story)] || [];
                                    const sentenceStart = tsArr[drillIndex] !== undefined ? tsArr[drillIndex] / 1000 : 0;
                                    const sentenceEnd = tsArr[drillIndex + 1] !== undefined
                                        ? tsArr[drillIndex + 1] / 1000
                                        : sentenceStart + 10;
                                    const sentenceDuration = Math.max(sentenceEnd - sentenceStart, 0.001);
                                    const elapsed = Math.min(Math.max((currentTime / 1000) - sentenceStart, 0), sentenceDuration);
                                    return (
                                        <div className="w-full mb-6">
                                            <div className="flex items-center gap-3">
                                                <span className="text-gray-500 text-xs font-mono min-w-[32px] text-right">
                                                    {formatTime(elapsed)}
                                                </span>
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={sentenceDuration || 0.001}
                                                    step={0.01}
                                                    value={elapsed}
                                                    onChange={(e) => {
                                                        const val = parseFloat(e.target.value);
                                                        const m = getMediaElem();
                                                        if (m && Number.isFinite(val)) {
                                                            const tsArr = timestamps[toLowerCamelCase(story)] || [];
                                                            const sentenceStart = tsArr[drillIndex] !== undefined ? tsArr[drillIndex] / 1000 : 0;
                                                            const seekTo = sentenceStart + val;
                                                            try { m.currentTime = seekTo; } catch (e) {}
                                                            setcurrentTime(seekTo * 1000);
                                                        }
                                                    }}
                                                    onMouseDown={() => { isSeekingRef.current = true; }}
                                                    onMouseUp={() => { isSeekingRef.current = false; }}
                                                    onTouchStart={() => { isSeekingRef.current = true; }}
                                                    onTouchEnd={() => { isSeekingRef.current = false; }}
                                                    className="flex-1 h-1 rounded-full appearance-none cursor-pointer"
                                                    style={{
                                                        background: sentenceDuration
                                                            ? `linear-gradient(to right, #22d3ee ${(elapsed / sentenceDuration) * 100}%, rgba(255,255,255,0.1) ${(elapsed / sentenceDuration) * 100}%)`
                                                            : 'rgba(255,255,255,0.1)',
                                                    }}
                                                />
                                                <span className="text-gray-500 text-xs font-mono min-w-[32px]">
                                                    {formatTime(sentenceDuration)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Camera Preview - shown when recording */}
                                {drillVideoRecording && (
                                    <div className="mb-6 flex justify-center">
                                        <div className="relative">
                                            <video
                                                ref={drillCameraPreviewRef}
                                                autoPlay
                                                muted
                                                playsInline
                                                className="rounded-2xl border-2 border-red-500 shadow-2xl shadow-red-500/30"
                                                style={{ width: '400px', maxWidth: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
                                            />
                                            {/* Recording indicator */}
                                            <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse flex items-center gap-2">
                                                <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
                                                Đang ghi hình
                                            </div>
                                            {/* Mic indicator */}
                                            <div className="absolute top-3 left-3 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-2">
                                                <span className="text-sm">🎤</span>
                                                Mic {drillListening ? 'ON' : 'OFF'}
                                            </div>
                                            {/* Transcript preview at bottom */}
                                            {drillRecordingTranscript && (
                                                <div className="absolute bottom-3 left-3 right-3 bg-black/70 text-white px-3 py-2 rounded-lg text-xs max-h-20 overflow-y-auto">
                                                    <p className="text-gray-300 text-[10px] mb-1">Transcript:</p>
                                                    {drillRecordingTranscript}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Controls: Prev | Play/Pause | Next | Camera */}
                                <div className="flex items-center justify-center gap-6 mb-6">
                                    <button
                                        onClick={() => drillIndex > 0 && playDrillSentence(drillIndex - 1)}
                                        disabled={drillIndex === 0}
                                        className="w-14 h-14 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                        title="Câu trước"
                                    >
                                        <StepBackwardOutlined className="text-2xl" />
                                    </button>

                                    <button
                                        onClick={async () => {
                                            if (drillIsPlaying) {
                                                clearDrillInterval();
                                                audioElem.current?.pause();
                                                setDrillIsPlaying(false);
                                            } else {
                                                if (!audioElem.current) return;
                                                const tsArr = timestamps[toLowerCamelCase(story)] || [];
                                                const sentenceStart = tsArr[drillIndex] !== undefined ? tsArr[drillIndex] / 1000 : 0;
                                                const sentenceEnd = tsArr[drillIndex + 1] !== undefined
                                                    ? tsArr[drillIndex + 1] / 1000
                                                    : sentenceStart + 10;
                                                // If audio ended or is past the sentence end, restart from sentence start
                                                const ct = audioElem.current.currentTime;
                                                if (audioElem.current.ended || ct >= sentenceEnd || ct < sentenceStart) {
                                                    audioElem.current.currentTime = sentenceStart;
                                                    drillEndRef.current = sentenceEnd;
                                                }
                                                audioElem.current.playbackRate = speedModeRef.current;
                                                try { await audioElem.current.play(); } catch (e) { return; }
                                                setDrillIsPlaying(true);
                                                startDrillInterval();
                                            }
                                        }}
                                        className={`w-20 h-20 rounded-full text-white flex items-center justify-center shadow-xl transition-all ${drillIsPlaying
                                                ? 'bg-cyan-400 shadow-cyan-400/40 scale-95 animate-pulse'
                                                : 'bg-cyan-500 hover:bg-cyan-600 shadow-cyan-500/30'
                                            }`}
                                        title={drillIsPlaying ? 'Dừng' : 'Phát câu này'}
                                    >
                                        {drillIsPlaying ? <PauseCircleFilled className="text-4xl" /> : <PlayCircleFilled className="text-4xl" />}
                                    </button>

                                    <button
                                        onClick={() => drillIndex < (lyric?.length || 1) - 1 && playDrillSentence(drillIndex + 1)}
                                        disabled={drillIndex >= (lyric?.length || 1) - 1}
                                        className="w-14 h-14 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                        title="Câu tiếp"
                                    >
                                        <StepForwardOutlined className="text-2xl" />
                                    </button>
                                </div>

                                {/* ── Answer input ─────────────────────────────────────── */}
                                <div className="w-full bg-[#1a2d4a] rounded-2xl p-5 border border-white/10 mb-4">


                                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-3 flex items-center justify-between">
                                        <span>Gõ lại những gì bạn nghe được</span>
                                        {drillListening && <span className="text-red-400 text-xs animate-pulse">● Đang nghe...</span>}
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={async () => {
                                                if (drillVideoRecording) {
                                                    // Video recording mode - do nothing on mic button
                                                    message.info('Vui lòng dùng nút "Dừng ghi hình" ở sidebar');
                                                    return;
                                                }
                                                // Normal mic mode
                                                if (drillListening) {
                                                    SpeechRecognition.stopListening();
                                                    setDrillListening(false);
                                                } else {
                                                    if (!browserSupportsSpeechRecognition) {
                                                        message.error('Trình duyệt không hỗ trợ nhận diện giọng nói');
                                                        return;
                                                    }
                                                    resetDrillTranscript();
                                                    try {
                                                        await SpeechRecognition.startListening({ language: 'en-US', continuous: false });
                                                        setDrillListening(true);
                                                    } catch (err) {
                                                        console.error('Speech recognition error:', err);
                                                        message.error('Không thể khởi động mic');
                                                    }
                                                }
                                            }}
                                            className={`w-12 h-12 flex-shrink-0 rounded-xl transition-all flex items-center justify-center ${drillVideoRecording ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30' :
                                                    drillListening
                                                        ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/30'
                                                        : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                                                }`}
                                            title={drillVideoRecording ? 'Chế độ ghi hình' : (drillListening ? 'Dừng lắng nghe' : 'Nói để nhập')}
                                        >
                                            {drillVideoRecording ? <VideoCameraOutlined className="text-lg" /> : <AudioFilled className="text-lg" />}
                                        </button>
                                        <input
                                            type="text"
                                            value={drillAnswer}
                                            onChange={e => { setDrillAnswer(e.target.value); setDrillAnswerResult(null); }}
                                            onKeyDown={e => e.key === 'Enter' && checkDrillAnswer()}
                                            placeholder="Nhập câu trả lời hoặc bấm mic để nói..."
                                            className="flex-1 bg-[#0f1e35] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-cyan-500/50"
                                        />
                                        <button
                                            onClick={checkDrillAnswer}
                                            disabled={!drillAnswer.trim()}
                                            className="px-4 py-2.5 rounded-xl bg-cyan-500 text-white text-sm font-medium hover:bg-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1.5 flex-shrink-0"
                                        >
                                            <CheckOutlined /> Kiểm tra
                                        </button>
                                    </div>

                                    {/* Result display */}
                                    {drillAnswerResult && (
                                        <div className="mt-4">
                                            <div className="flex items-center gap-4 mb-3">
                                                <Progress
                                                    type="circle" size={56}
                                                    percent={drillAnswerResult.point}
                                                    strokeColor={drillAnswerResult.point >= 80 ? '#22c55e' : drillAnswerResult.point >= 50 ? '#f59e0b' : '#ef4444'}
                                                    trailColor="rgba(255,255,255,0.1)"
                                                    format={p => <span className="text-white font-bold text-sm">{p}%</span>}
                                                />
                                                <div>
                                                    <p className={`font-bold text-lg ${drillAnswerResult.point >= 80 ? 'text-green-400' : drillAnswerResult.point >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                                                        {drillAnswerResult.point >= 80 ? '🎉 Xuất sắc!' : drillAnswerResult.point >= 50 ? '👍 Khá tốt!' : '💪 Cần luyện thêm!'}
                                                    </p>
                                                    <p className="text-gray-400 text-xs">Độ chính xác</p>
                                                </div>
                                            </div>
                                            <div className="bg-[#0f1e35] rounded-xl px-4 py-3 flex flex-wrap gap-x-1.5 gap-y-1">
                                                {drillAnswerResult.words.map(({ word, matched }, i) => (
                                                    <span key={i} className={`font-semibold text-sm ${matched ? 'text-green-400' : 'text-red-400'}`}>
                                                        {word}
                                                    </span>
                                                ))}
                                            </div>

                                            {/* IPA */}
                                            {showIPA && (getIpaForIndex(drillIndex) !== null) && (
                                                <div className="mt-3 bg-[#0f1e35] rounded-xl px-4 py-2 border border-cyan-500/20">
                                                    <p className="text-cyan-400 text-xs mb-1 uppercase tracking-wider">Phiên âm IPA:</p>
                                                    <p className="text-cyan-300 font-mono text-sm">{getIpaForIndex(drillIndex) || <span className="opacity-30">—</span>}</p>
                                                </div>
                                            )}

                                            {/* Translation */}
                                            {showTranslation && translations[drillIndex] && (
                                                <div className="mt-2 bg-[#0f1e35] rounded-xl px-4 py-2 border border-yellow-500/20">
                                                    <p className="text-yellow-400 text-xs mb-1 uppercase tracking-wider">Bản dịch:</p>
                                                    <p className="text-white text-sm not-italic">{cleanTranslation(translations[drillIndex])}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Sidebar: Settings & Camera - 1 column */}
                            <div className={`lg:col-span-1 space-y-4 ${showDrillSettings ? '' : 'hidden lg:block'}`}>
                                {/* Settings Panel */}
                                <div className="bg-[#1a2d4a] rounded-2xl p-5 border border-white/10 sticky top-20">
                                    <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Cài đặt</h3>
                                    <div className="space-y-4">
                                        {/* Speed */}
                                        <div>
                                            <p className="text-gray-400 text-xs mb-2 uppercase tracking-wider flex items-center gap-1">
                                                ⚡ Tốc độ phát
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {speeds.map(s => (
                                                    <button key={s} onClick={() => changeSpeedMode(s)}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${speedMode === s
                                                                ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/30'
                                                                : 'bg-white/10 text-gray-300 hover:bg-white/20'
                                                            }`}>
                                                        {s}x
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="border-t border-white/10" />

                                        <div className="border-t border-white/10" />


                                        {/* Auto-next */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-400 text-xs uppercase tracking-wider flex items-center gap-1">
                                                ⏭ Tự động sang câu tiếp
                                            </span>
                                            <Switch
                                                size="small"
                                                checked={drillAutoNext}
                                                onChange={v => setDrillAutoNext(v)}
                                                className={`${drillAutoNext ? '!bg-cyan-500' : '!bg-gray-600'}`}
                                            />
                                        </div>

                                    </div>
                                </div>

                                {/* Tools panel: replace recording controls with navigation buttons */}
                                <div className="bg-[#1a2d4a] rounded-2xl p-5 border border-white/10">
                                    <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Công cụ</h3>
                                    <div className="space-y-3">
                                        <button
                                            onClick={closeDrillMode}
                                            className="flex items-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-white/5 text-white hover:bg-white/10"
                                        >
                                            <HomeOutlined className="text-lg" /> Bài học
                                        </button>

                                        <button
                                            onClick={() => navigate(`/lesson/speaking/${slug}`, { state: { index } })}
                                            className="flex items-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-cyan-500 text-white hover:bg-cyan-600"
                                        >
                                            <SoundOutlined className="text-lg" /> Luyện nói
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* ── Normal mode ───────────────────────────────────────────── */}
                {!showDrillMode && (
                    <>

                        {/* Track Selector */}
                        <div className="flex justify-center mb-6">
                            <div className="bg-[#1a2d4a] rounded-xl p-1 inline-flex gap-1 flex-wrap justify-center">
                                {options.map(opt => (
                                    <button
                                        key={opt}
                                        onClick={() => handleChangeStory(opt)}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${story === opt
                                                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                                                : 'text-blue-200 hover:text-white hover:bg-white/10'
                                            }`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Left: Lyrics Panel */}
                            <div className="lg:col-span-2">
                                <div className="bg-[#1a2d4a] rounded-2xl p-2 shadow-xl border border-white/5">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-white font-semibold flex items-center gap-2">
                                            <SoundOutlined className="text-blue-400" /> Lyrics / Transcript
                                        </h3>
                                        <div className="flex items-center gap-3">
                                            <span className="text-blue-300 text-sm bg-blue-500/10 px-3 py-1 rounded-full">
                                                Câu {active + 1}/{ts.length || 0}
                                            </span>
                                        </div>
                                    </div>

                                    {/* LRC Display */}
                                    <div ref={lyricsContainerRef} className="bg-[#0f1e35] rounded-xl p-2 max-h-[48vh] md:max-h-[60vh] lg:max-h-[520px] overflow-y-auto scrollbar-thin">
                                        {lyric?.map((line, i) => (
                                            <div
                                                key={i}
                                                data-lyric-index={i}
                                                onClick={() => playSentenceOnly(i)}
                                                className={`px-3 py-2 rounded-lg cursor-pointer mb-1 transition-all ${(active === i || playingSentence === i)
                                                        ? 'bg-blue-500/20 text-yellow-300'
                                                        : 'text-gray-400 hover:text-gray-200 hover:bg-white/5 border-l-4 border-transparent'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    {/* Container for text + IPA + translation */}
                                                    <div className="flex-1 min-w-0">
                                                        {/* IPA - shown above, only for active/playing sentence */}
                                                        {showIPA && (active === i || playingSentence === i) && (
                                                            <div className="mb-1 text-cyan-400/80 font-mono" style={{ fontSize: `${Math.max(subtitleFontSize - 4, 10)}px` }}>
                                                                {(getIpaForIndex(i) === null) ? (
                                                                    <span className="opacity-40 animate-pulse">● ● ● ● ●</span>
                                                                ) : (
                                                                    (getIpaForIndex(i) || <span className="opacity-30">—</span>)
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Lyric text — hidden/blurred when hideSubtitle + this sentence is playing; press to temporarily reveal */}
                                                        {/* If this lesson needs numbering at line start, prefix number here */}
                                                        <span
                                                            className={`${active === i ? 'font-semibold' : ''} transition-all select-none flex items-center`}
                                                            style={{ fontSize: `${subtitleFontSize}px` }}
                                                        >
                                                            {((hiddenLines[i] === false) || (showTranscript && !hiddenLines[i])) ? line : <span className="opacity-40 italic text-gray-500">(đã ẩn)</span>}
                                                        </span>

                                                        {/* Translation - shown below each sentence when enabled */}
                                                        {showTranslation && (active === i || playingSentence === i) && ((hiddenLines[i] === false) || (showTranscript && !hiddenLines[i])) && (
                                                            <div className="mt-1 text-white not-italic" style={{ fontSize: `${translationFontSize}px` }}>
                                                                {translationLoading ? (
                                                                    <span className="opacity-40 animate-pulse">Đang dịch...</span>
                                                                ) : (
                                                                    (cleanTranslation(translations[i]) || <span className="opacity-30">—</span>)
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Right-side: show index and eye icon when transcript hidden */}
                                                    <div className="flex items-center gap-2 ml-3">
                                                        {/* Show index on the right for lessons except bai-01-day-of-the-dead (which uses prefixed numbering) */}

                                                        {/* Eye icon: only show for this lesson when Transcript is hidden in settings;
                                                        otherwise keep existing behavior for other lessons */}
                                                        {(slug === 'bai-01-day-of-the-dead' ? (!showTranscript) : (!lessonHasSpecialUi() || !showTranscript)) && (
                                                            <button
                                                                type="button"
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setHiddenLines(prev => {
                                                                        const curr = prev[i];
                                                                        const currentlyShown = ((curr === false) || (showTranscript && !curr));
                                                                        // nextVal: true = hidden, false = shown
                                                                        const nextVal = currentlyShown ? true : false;
                                                                        const next = { ...prev, [i]: nextVal };
                                                                        try { sessionStorage.setItem(`hiddenLines_${slug}`, JSON.stringify(next)); } catch (err) { }

                                                                        // If user is revealing the transcript (nextVal === false), pause media;
                                                                        // if user is hiding the transcript again (nextVal === true), resume play.
                                                                        try {
                                                                            const media = getMediaElem();
                                                                            if (media) {
                                                                                if (!nextVal) {
                                                                                    // showing transcript -> pause
                                                                                    try { if (!media.paused) media.pause(); } catch (e) { }
                                                                                    try { setisPlaying(false); } catch (e) { }
                                                                                } else {
                                                                                    // hiding transcript -> play
                                                                                    try { media.play(); } catch (e) { }
                                                                                    try { setisPlaying(true); } catch (e) { }
                                                                                }
                                                                            }
                                                                        } catch (e) { /* ignore */ }

                                                                        return next;
                                                                    });
                                                                }}
                                                                className="w-8 h-8 rounded-full flex items-center justify-center bg-white/5 text-gray-300 hover:bg-white/10 ml-2"
                                                                title="Nhấn để hiện/ẩn transcript"
                                                            >
                                                                {((hiddenLines[i] === false) || (showTranscript && !hiddenLines[i])) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                </div>
                            </div>

                            {/* Right: Controls Panel */}
                            <div className="lg:col-span-1 space-y-4">
                                {/* Action Buttons */}
                                <div className="bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                                    <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Công cụ</h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Tooltip title="Chuyển sang trang luyện nói">
                                            <button
                                                onClick={() => {
                                                    const vipExpireAt = userInfo?.vip_expire_at || cookies?.user?.vip_expire_at;
                                                    const hasVipAccess = (dayjsInstance(vipExpireAt)?.format("YYYY-MM-DD") > dayjsInstance(Date())?.format("YYYY-MM-DD") && vipExpireAt !== null);
                                                    if (!hasVipAccess) {
                                                        message.warning('Chức năng này dành cho người dùng VIP. Vui lòng nâng cấp tài khoản!');
                                                        return;
                                                    }
                                                    navigate(`/lesson/speaking/${slug}`, { state: { index } });
                                                }}
                                                className="col-span-2 flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-green-500/10 text-green-400 hover:bg-green-500/20"
                                            >
                                                <span className="text-lg">🗣️</span> Luyện nói
                                            </button>
                                        </Tooltip>
                                        <Tooltip title="Luyện nghe từng câu — nghe audio từng câu, có Prev/Next">
                                            <button
                                                onClick={() => {
                                                    const vipExpireAt = userInfo?.vip_expire_at || cookies?.user?.vip_expire_at;
                                                    const hasVipAccess = (dayjsInstance(vipExpireAt)?.format("YYYY-MM-DD") > dayjsInstance(Date())?.format("YYYY-MM-DD") && vipExpireAt !== null);
                                                    if (!hasVipAccess) {
                                                        message.warning('Chức năng này dành cho người dùng VIP. Vui lòng nâng cấp tài khoản!');
                                                        return;
                                                    }
                                                    openDrillMode();
                                                }}
                                                className="col-span-2 flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20"
                                            >
                                                <span className="text-lg">🎧</span> Luyện nghe
                                            </button>
                                        </Tooltip>
                                        <button onClick={() => setShowSettings(!showSettings)}
                                            className="col-span-2 flex items-center gap-2 bg-blue-500/10 text-blue-400 px-4 py-3 rounded-xl hover:bg-blue-500/20 transition-all text-sm font-medium w-full">
                                            <SettingOutlined className="text-lg" /> Cài đặt
                                        </button>
                                    </div>
                                </div>

                                {/* Settings Panel */}
                                {showSettings && (
                                    <div className="bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                                        <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Cài đặt</h3>
                                        <div className="space-y-4">
                                            <div>
                                                <p className="text-gray-400 text-xs mb-2">Tốc độ phát</p>
                                                <div className="flex flex-wrap gap-2">
                                                    {speeds.map(s => (
                                                        <button key={s} onClick={() => changeSpeedMode(s)}
                                                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${speedMode === s
                                                                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                                                                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                                                                }`}>
                                                            {s}x
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="border-t border-white/10 my-2"></div>

                                            <div>
                                                <p className="text-gray-400 text-xs mb-2 flex items-center justify-between">
                                                    <span>Lặp lại mỗi câu: {loopMode !== 1 ? 'Tắt' : (sentenceRepeatLimit === 0 ? 'Vô hạn' : `${sentenceRepeatLimit} lần`)}</span>
                                                </p>
                                                <Slider
                                                    min={0}
                                                    max={11}
                                                    value={loopMode === 0 ? 0 : (sentenceRepeatLimit === 0 ? 11 : (sentenceRepeatLimit > 10 ? 11 : sentenceRepeatLimit))}
                                                    onChange={v => {
                                                        if (v === 0) { setLoopMode(0); }
                                                        else if (v === 11) { setSentenceRepeatLimit(0); setLoopMode(1); }
                                                        else { setSentenceRepeatLimit(v); setLoopMode(1); }
                                                    }}
                                                    className="[&_.ant-slider-track]:bg-blue-500 [&_.ant-slider-handle]:border-blue-500"
                                                />
                                            </div>

                                            {/* Auto Scroll Toggle */}
                                            <div className="flex items-center justify-between">
                                                <p className="text-gray-400 text-xs flex items-center gap-1">
                                                    <RetweetOutlined /> Tự động cuộn
                                                </p>
                                                <Switch
                                                    size="small"
                                                    checked={autoScroll}
                                                    onChange={v => setAutoScroll(v)}
                                                    className={autoScroll ? '!bg-blue-500' : ''}
                                                />
                                            </div>

                                            {/* Divider */}
                                            <div className="border-t border-white/10 my-2"></div>

                                            {/* Font Size Controls */}
                                            <div>
                                                <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                                                    <FontSizeOutlined /> Cỡ chữ phụ đề: {subtitleFontSize}px
                                                </p>
                                                <Slider
                                                    min={12}
                                                    max={28}
                                                    step={1}
                                                    value={subtitleFontSize}
                                                    onChange={v => setSubtitleFontSize(v)}
                                                    className="[&_.ant-slider-track]:bg-blue-500 [&_.ant-slider-handle]:border-blue-500"
                                                />
                                            </div>

                                            <div>
                                                <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                                                    <FontSizeOutlined /> Cỡ chữ bản dịch: {translationFontSize}px
                                                </p>
                                                <Slider
                                                    min={10}
                                                    max={24}
                                                    step={1}
                                                    value={translationFontSize}
                                                    onChange={v => setTranslationFontSize(v)}
                                                    className="[&_.ant-slider-track]:bg-blue-500 [&_.ant-slider-handle]:border-blue-500"
                                                />
                                            </div>

                                            {/* Divider */}
                                            <div className="border-t border-white/10 my-2"></div>

                                            {/* Translations and IPA are always shown on listening page */}
                                            <div className="flex items-center justify-between">
                                                <p className="text-gray-400 text-xs flex items-center gap-1">
                                                    <EyeOutlined /> Hiển thị bản dịch
                                                </p>
                                                <Switch
                                                    size="small"
                                                    checked={showTranslation}
                                                    onChange={v => setShowTranslation(v)}
                                                    className={showTranslation ? '!bg-blue-500' : ''}
                                                />
                                            </div>

                                            <div className="flex items-center justify-between mt-2">
                                                <p className="text-gray-400 text-xs flex items-center gap-1">
                                                    <EyeInvisibleOutlined /> Hiển thị IPA
                                                </p>
                                                <Switch
                                                    size="small"
                                                    checked={showIPA}
                                                    onChange={v => setShowIPA(v)}
                                                    className={showIPA ? '!bg-blue-500' : ''}
                                                />
                                            </div>

                                            <div className="flex items-center justify-between mt-2">
                                                <p className="text-gray-400 text-xs flex items-center gap-1">
                                                    <EyeOutlined /> Hiển thị Transcript
                                                </p>
                                                <Switch
                                                    size="small"
                                                    checked={showTranscript}
                                                    onChange={v => setShowTranscript(v)}
                                                    className={showTranscript ? '!bg-blue-500' : ''}
                                                />
                                            </div>
                                            <div className="border-t border-white/10 my-2"></div>
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>

                        {/* Bottom Audio Controls (fixed) */}
                        <div className="fixed bottom-0 left-0 right-0 bg-[#0a1628]/95 backdrop-blur-xl border-t border-white/10 z-30">
                            {/* Progress bar — full width */}
                            <div className="max-w-screen-xl mx-auto px-3 pt-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-400 text-xs font-mono w-9 text-right flex-shrink-0">
                                        {formatTime(currentTime / 1000)}
                                    </span>
                                    <input
                                        type="range"
                                        min={0}
                                        max={progressMaxSec || 0.001}
                                        step={0.1}
                                        value={progressValueSec}
                                        onChange={e => {
                                            const val = parseFloat(e.target.value);
                                            const m = getMediaElem();
                                            if (m && Number.isFinite(val)) {
                                                lastSeekValueRef.current = val;
                                                // Ensure totalDuration is valid before seeking
                                                if (!totalDuration || totalDuration < 0.1) handleDurationUpdate();
                                                
                                                try { m.currentTime = val; } catch (e) {}
                                                setcurrentTime(val * 1000);
                                                if (Number.isFinite(progressMaxSec) && progressMaxSec > 0 && val >= (progressMaxSec - 0.3)) {
                                                    triggerReloadOnEnd(true);
                                                }
                                                const tsKey = toLowerCamelCase(story);
                                                const tsArr = timestamps[tsKey] || [];
                                                let newActive = 0;
                                                for (let i = 0; i < tsArr.length; i++) {
                                                    if (val * 1000 >= (tsArr[i] || 0)) newActive = i;
                                                    else break;
                                                }
                                                setActive(newActive);
                                            }
                                        }}
                                        onMouseDown={() => { isSeekingRef.current = true; }}
                                        onMouseUp={() => {
                                            isSeekingRef.current = false;
                                            if (Number.isFinite(progressMaxSec) && progressMaxSec > 0 && lastSeekValueRef.current >= (progressMaxSec - 0.3)) {
                                                triggerReloadOnEnd(true);
                                            }
                                        }}
                                        onTouchStart={() => { isSeekingRef.current = true; }}
                                        onTouchEnd={() => {
                                            isSeekingRef.current = false;
                                            if (Number.isFinite(progressMaxSec) && progressMaxSec > 0 && lastSeekValueRef.current >= (progressMaxSec - 0.3)) {
                                                triggerReloadOnEnd(true);
                                            }
                                        }}
                                        style={{
                                            background: progressMaxSec
                                                ? `linear-gradient(to right, #3b82f6 ${(progressValueSec / progressMaxSec) * 100}%, rgba(255,255,255,0.1) ${(progressValueSec / progressMaxSec) * 100}%)`
                                                : 'rgba(255,255,255,0.1)',
                                            accentColor: '#3b82f6',
                                        }}
                                        className="flex-1 h-1.5 rounded-lg appearance-none cursor-pointer"
                                    />
                                    <span className="text-gray-400 text-xs font-mono w-9 flex-shrink-0">
                                        {formatTime(progressMaxSec || 0)}
                                    </span>
                                </div>
                            </div>

                            {/* Controls row */}
                            <div className="max-w-screen-xl mx-auto px-3 py-2">
                                <div className="flex items-center gap-2">

                                    {/* LEFT: Speed button — always visible far left */}
                                    <button
                                        onClick={() => {
                                            const idx = speeds.indexOf(speedMode);
                                            changeSpeedMode(speeds[(idx + 1) % speeds.length]);
                                        }}
                                        className="bg-white/10 text-blue-300 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-white/20 transition-all flex-shrink-0 min-w-[44px] text-center"
                                    >
                                        {speedMode}x
                                    </button>

                                    {/* CENTER: Prev / Play / Next — takes remaining space, centered */}
                                    <div className="flex-1 flex items-center justify-center gap-4 sm:gap-6">
                                        <button
                                            onClick={() => {
                                                const nextActive = Math.max(0, active - 1);
                                                setActive(nextActive);
                                                const m = getMediaElem();
                                                if (m && ts[nextActive] !== undefined) {
                                                    m.currentTime = ts[nextActive] / 1000;
                                                    setcurrentTime(ts[nextActive]);
                                                    if (isPlaying) { safePlay(); }
                                                }
                                                if (!isPlaying) pause();
                                            }}
                                            className="text-gray-300 hover:text-white p-2 transition-colors"
                                        >
                                            <StepBackwardOutlined className="text-xl" />
                                        </button>
                                        <button
                                            onClick={PlayPause}
                                            className="bg-blue-500 hover:bg-blue-600 text-white w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30 transition-all hover:scale-105 flex-shrink-0"
                                        >
                                            {isPlaying
                                                ? <PauseCircleFilled className="text-xl sm:text-2xl" />
                                                : <PlayCircleFilled className="text-xl sm:text-2xl" />}
                                        </button>
                                        <button
                                            onClick={() => {
                                                const nextActive = (active + 1) % (ts.length || 1);
                                                setActive(nextActive);
                                                const m = getMediaElem();
                                                if (m && ts[nextActive] !== undefined) {
                                                    m.currentTime = ts[nextActive] / 1000;
                                                    setcurrentTime(ts[nextActive]);
                                                    if (isPlaying) { safePlay(); }
                                                }
                                                if (!isPlaying) pause();
                                            }}
                                            className="text-gray-300 hover:text-white p-2 transition-colors"
                                        >
                                            <StepForwardOutlined className="text-xl" />
                                        </button>
                                    </div>

                                    {/* RIGHT: Loop + Volume */}
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {/* Loop button */}
                                        <div className="relative">
                                            <button
                                                onClick={() => {
                                                    // Level 0 (Off) -> Level 1 (Loop 1 time) -> Level 2 (Loop Infinity)
                                                    if (!repeatWholeTrack && replay === 1) {
                                                        setRepeatWholeTrack(false); setReplay(2);
                                                    } else if (!repeatWholeTrack && replay === 2) {
                                                        setRepeatWholeTrack(true); setReplay(1);
                                                    } else {
                                                        setRepeatWholeTrack(false); setReplay(1);
                                                    }
                                                }}
                                                title={repeatWholeTrack ? 'Lặp cả bài (Vô hạn)' : replay === 2 ? 'Lặp cả bài (1 lần)' : 'Không lặp'}
                                                className={`rounded-md p-2 transition-all flex items-center justify-center relative ${repeatWholeTrack || replay === 2 ? 'bg-blue-500/20 text-white shadow-lg' : 'bg-transparent text-gray-300 opacity-40'
                                                    }`}
                                            >
                                                <img src={repeatTrack} alt="repeat" className="w-5 h-5" />
                                                {(repeatWholeTrack || replay === 2) && (
                                                    <div className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] font-extrabold rounded-full w-4 h-4 flex items-center justify-center border border-white/10">
                                                        {repeatWholeTrack ? '∞' : '1'}
                                                    </div>
                                                )}
                                            </button>

                                        </div>
                                        {/* Volume — hidden on mobile, visible sm+ */}
                                        <div className="flex items-center gap-1.5">
                                            {lessonHasSpecialUi() ? (
                                                <AudioFilled className="text-gray-400 text-sm" />
                                            ) : (
                                                <SoundOutlined className="text-gray-400 text-sm" />
                                            )}
                                            <input
                                                type="range"
                                                min={0}
                                                max={1}
                                                step={0.01}
                                                value={volume}
                                                onChange={(e) => setVolume(parseFloat(e.target.value))}
                                                className="w-20 h-1 accent-blue-500"
                                                aria-label="Volume"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Drill Video Recording Summary Modal */}
            <Modal
                open={showDrillRecordingModal}
                onOk={() => setShowDrillRecordingModal(false)}
                onCancel={() => setShowDrillRecordingModal(false)}
                width={600}
                footer={[
                    <Button key="ok" type="primary" onClick={() => setShowDrillRecordingModal(false)}
                        className="bg-blue-500">
                        Đóng
                    </Button>
                ]}
            >
                <div className="py-4">
                    <h2 className="text-2xl font-bold text-center mb-4 text-blue-600">
                        🎥 Kết quả ghi hình
                    </h2>

                    <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 mb-4">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <CheckCircleOutlined className="text-green-500 text-3xl" />
                            <p className="text-lg font-bold text-gray-800">Hoàn thành ghi hình!</p>
                        </div>
                        <p className="text-center text-gray-600 text-sm">
                            Video đã được lưu vào máy của bạn
                        </p>
                    </div>

                    {drillRecordingTranscript && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                <SoundOutlined className="text-blue-500" />
                                Lời bạn đã nói:
                            </h3>
                            <p className="text-gray-800 leading-relaxed">
                                {drillRecordingTranscript || <span className="text-gray-400 italic">Không phát hiện lời nói</span>}
                            </p>
                        </div>
                    )}

                    <div className="mt-4 text-center">
                        <p className="text-sm text-gray-600">
                            💪 Tiếp tục luyện tập để cải thiện kỹ năng!
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
